import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import guardianDashboardService from '../services/guardianDashboardService';
import employmentHistoryService from '../services/employmentHistoryService';
import rentsService from '../services/rentsService';
import guardiansService from '../services/guardiansService';
import styles from '../assets/guardianDashboard.module.css';
import GuardianStageHeader from '../components/GuardianStageHeader';
import S3DocumentUpload from '../components/S3DocumentUpload';
import DateInput from '../components/DateInput';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const GuardianDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendForm, setRecommendForm] = useState({
    name: '',
    phone_number: '',
    email: '',
  });
  const [newsletterChecked, setNewsletterChecked] = useState(false);
  const [voluntaryWorkChecked, setVoluntaryWorkChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [stage1Reqs, setStage1Reqs] = useState([]);
  const [stage1Docs, setStage1Docs] = useState({});
  const [submittingStage1, setSubmittingStage1] = useState(false);

  // Key collection state (Laravel: final-message.blade.php)
  const [keyCollectionDescription, setKeyCollectionDescription] = useState('');
  const [submittingKeyCollection, setSubmittingKeyCollection] = useState(false);

  // License signature date state (Laravel: sign_agreement.blade.php)
  const [signatureDate, setSignatureDate] = useState(new Date().toLocaleDateString('en-GB'));

  // Employment history state
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [loadingEmployment, setLoadingEmployment] = useState(false);
  const [showEmploymentForm, setShowEmploymentForm] = useState(false);

  // Guardian history (rents) state for previous guardian
  const [guardianRents, setGuardianRents] = useState([]);
  const [loadingGuardianRents, setLoadingGuardianRents] = useState(false);
  const [guardianHistorySearch, setGuardianHistorySearch] = useState('');
  const [guardianHistoryPage, setGuardianHistoryPage] = useState(1);
  const [guardianHistoryEntriesPerPage, setGuardianHistoryEntriesPerPage] = useState(10);

  // State for employment history table sorting
  const [employmentSortField, setEmploymentSortField] = useState("date_start");
  const [employmentSortDirection, setEmploymentSortDirection] = useState("desc");

  // Handle employment history sorting
  const handleEmploymentSort = (field) => {
    if (employmentSortField === field) {
      setEmploymentSortDirection(employmentSortDirection === "asc" ? "desc" : "asc");
    } else {
      setEmploymentSortField(field);
      setEmploymentSortDirection("asc");
    }
  };

  // Sort employment history for display
  const sortedEmploymentHistory = [...employmentHistory].sort((a, b) => {
    let aValue = a[employmentSortField];
    let bValue = b[employmentSortField];

    // Handle nested object sorting - map sortField to actual data field names
    if (employmentSortField === "company_name") {
      aValue = a.company_name || "";
      bValue = b.company_name || "";
    } else if (employmentSortField === "date_start") {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    } else if (employmentSortField === "date_end") {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    }

    if (aValue < bValue) return employmentSortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return employmentSortDirection === "asc" ? 1 : -1;
    return 0;
  });
  const [employmentForm, setEmploymentForm] = useState({
    date_start: '',
    date_end: '',
    still_in_employment: '0',
    company_name: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    referee_name: '',
    referee_telephone: '',
    referee_email: '',
    job_title: '',
    reason_for_leaving: '',
  });
  const [submittingEmployment, setSubmittingEmployment] = useState(false);
  const [editingEmploymentId, setEditingEmploymentId] = useState(null);
  const [employmentFormErrors, setEmploymentFormErrors] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployment, setSelectedEmployment] = useState(null);
  const [deletingEmploymentId, setDeletingEmploymentId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stage 1 personal details state
  const [profilePicKey, setProfilePicKey] = useState('');
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [heardAbout, setHeardAbout] = useState('');
  const [heardAboutOptions, setHeardAboutOptions] = useState([]); // ✅ NEW: Dropdown options
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [criminalRecord, setCriminalRecord] = useState(null); // true/false
  const [criminalRecordDescription, setCriminalRecordDescription] = useState(''); // ✅ NEW: Description field
  const [medicalCondition, setMedicalCondition] = useState(null); // true/false
  const [medicalConditionDescription, setMedicalConditionDescription] = useState(''); // ✅ NEW: Description field
  const [nextOfKin, setNextOfKin] = useState('');

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // ✅ Track user interaction
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false); // ✅ Track submission attempts

  // ✅ DEBUG: Monitor profile picture state changes
  useEffect(() => {
    console.log('[GUARDIAN DASHBOARD] profilePreviewUrl changed:', profilePreviewUrl);
  }, [profilePreviewUrl]);

  useEffect(() => {
    console.log('[GUARDIAN DASHBOARD] profilePicKey changed:', profilePicKey);
  }, [profilePicKey]);

  // Get application stage data from backend (matching Laravel structure)
  let applicationStage = dashboardData?.applicationStage;

  // Stage tracking state - following Laravel logic exactly
  const [currentStage, setCurrentStage] = useState({
    active: 'stage_2', // Stage 1: Documents Required (Laravel: stage_2 means Stage 1 is active)
    sent: false,
    paid: false,
    propertyAssigned: false,
  });

  // Document upload state
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch guardian rents for previous guardian (status 7) - matching Laravel guardian history table
  useEffect(() => {
    const fetchGuardianRents = async () => {
      const guardian = dashboardData?.guardian;
      const statusId = guardian?.status_id;
      
      // Only fetch for previous guardian (status 7) when showResources is true
      if (statusId === 7 && guardian?.id) {
        try {
          setLoadingGuardianRents(true);
          const guardianData = await guardiansService.getGuardianById(guardian.id);
          // Guardian data includes rents array from backend
          if (guardianData?.rents && Array.isArray(guardianData.rents)) {
            setGuardianRents(guardianData.rents);
          } else {
            setGuardianRents([]);
          }
        } catch (error) {
          console.error('Error fetching guardian rents:', error);
          setGuardianRents([]);
        } finally {
          setLoadingGuardianRents(false);
        }
      } else {
        setGuardianRents([]);
      }
    };

    if (dashboardData?.guardian) {
      fetchGuardianRents();
    }
  }, [dashboardData?.guardian?.id, dashboardData?.guardian?.status_id]);

  const fetchDashboardData = async () => {
    try {
      const data = await guardianDashboardService.getDashboardData();
      setDashboardData(data);
      setNewsletterChecked(data.guardian.on_newsletter_list === 1);
      setVoluntaryWorkChecked(data.guardian.on_voluntary_work_list === 1);

      // Store awaiting key collection state for Navbar menu display
      // Laravel: hides "Property Search" and shows "My Tickets" when:
      // - Status 5 (awaiting_key_collection), OR
      // - Status 4 (payment_pending) with payment_confirmed = 1
      const paymentConfirmed = data.latestRent?.payment_confirmed == 1 || data.latestRent?.payment_confirmed === true;
      const isAwaitingKeyCollection = data.guardian?.status_id === 5 ||
        (data.guardian?.status_id === 4 && paymentConfirmed);
      console.log('🔍 [NAVBAR] status_id:', data.guardian?.status_id, 'payment_confirmed:', data.latestRent?.payment_confirmed, 'isAwaitingKeyCollection:', isAwaitingKeyCollection);
      localStorage.setItem('guardian_awaiting_key_collection',
        isAwaitingKeyCollection ? 'true' : 'false');
      // Dispatch custom event to notify Navbar to re-render immediately
      window.dispatchEvent(new Event('guardianStatusChanged'));

      // Load existing stage 1 data if available (matching Laravel's approach)
      if (data.stage1Data) {
        setStage1Docs(data.stage1Data.documents || {});

        // ✅ FIX: Convert profile_picture string to object format for S3DocumentUpload
        const profilePic = data.stage1Data.profile_picture;
        if (profilePic && typeof profilePic === 'string') {
          setProfilePicKey({
            key: profilePic,
            name: profilePic.split('/').pop(), // Extract filename from S3 key
            uploadedAt: data.stage1Data.updated_at || new Date().toISOString()
          });
        } else if (profilePic && typeof profilePic === 'object') {
          setProfilePicKey(profilePic);
        } else {
          setProfilePicKey('');
        }

        setHeardAbout(data.stage1Data.heard_about || '');
        setDateOfBirth(data.stage1Data.date_of_birth || '');
        setCriminalRecord(
          data.stage1Data.criminal_record !== null
            ? data.stage1Data.criminal_record === 1
            : null
        );
        setCriminalRecordDescription(data.stage1Data.criminal_record_description || ''); // ✅ NEW
        setMedicalCondition(
          data.stage1Data.medical_condition !== null
            ? data.stage1Data.medical_condition === 1
            : null
        );
        setMedicalConditionDescription(data.stage1Data.medical_condition_description || ''); // ✅ NEW
        setNextOfKin(data.stage1Data.next_of_kin || '');
      }

      // ✅ NEW: Load hear about options (Laravel dropdown compatibility)
      if (data.heardAboutOptions) {
        setHeardAboutOptions(data.heardAboutOptions);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ IMPLEMENTATION 1: Laravel Status-Specific Content Mapping
  const getStatusSpecificContent = (guardian, applicationStage, latestRent) => {
    const statusId = guardian?.status_id;
    const firstName = user?.first_name || '';
    
    console.log('🎯 [DASHBOARD] Getting status-specific content for status_id:', statusId);
    
    switch (statusId) {
      case 1: // registered
      case 15: // documents_required_placement  
      case 13: // archived
        return {
          messageHeader: 'My messages',
          messageTitle: 'Welcome to Global Guardians',
          messageDescription: '',
          showResources: true,
          showAvailableSpaces: false,
          showNextSteps: false
        };

      case 7: // previous_guardian (Laravel: different message, shows guardian history, hides current property)
        return {
          messageHeader: 'My messages',
          messageTitle: 'Welcome to Global Guardians',
          messageDescription: 'Our Guardian Management Team are on the hunt to find the most suitable property for you! Please be patient so we can find the right property for you. In addition, you can take a look at our current available spaces <strong><a href="/spaces/available" class="text--mint" style="color: #2FC694;">here</a></strong> and book in for an appointment now!<br><br>Any feedback you have send through to <a href="mailto:guardiancare@global-guardians.co.uk" class="text--mint" style="color: #2FC694;">guardiancare@global-guardians.co.uk</a>',
          showResources: true, // Shows guardian history table
          showAvailableSpaces: true, // Shows available spaces section
          showNextSteps: false,
          showCurrentProperty: false, // Laravel: hides current property section for previous guardian
          showSignedAgreement: false // Laravel: hides signed agreement for previous guardian
        };

      case 2: // documents_required
      case 11: // documents_uploaded
        return {
          messageHeader: '',
          messageTitle: applicationStage?.sent ? 'Documents Uploaded' : 'Documents Required',
          messageDescription: applicationStage?.sent 
            ? 'Thank you for providing documentation. We are reviewing it and will get back to you once completed.'
            : 'Thank you for registering with Global Guardians! To become a guardian with us, please complete your application by providing the required documents.',
          showResources: true,
          showAvailableSpaces: false,
          showNextSteps: false
        };

      case 12: // vetted
      case 14: // vetted_ready
        return {
          messageHeader: '',
          messageTitle: 'Congratulations, on passing your vetting!',
          messageDescription: 'We are excited to inform you that you have successfully passed the vetting process to become a guardian with Global Guardians. Here\'s how to proceed:',
          showResources: true,
          showAvailableSpaces: true,
          showNextSteps: true,
          nextSteps: [
            {
              number: 1,
              title: 'Search for Properties',
              description: ['Click on the \'Search Properties\' button', 'Scroll through the list of available properties.']
            },
            {
              number: 2, 
              title: 'Request a Viewing',
              description: ['Click the \'Request a Viewing\' button.', 'Select a suitable date and time for the viewing.']
            },
            {
              number: 3,
              title: 'Keep Me Informed', 
              description: ['If no viewings are currently scheduled, click the \'Keep Me Informed\' button.', 'We will notify you when a viewing is scheduled.']
            },
            {
              number: 4,
              title: 'Attend the Viewing',
              description: ['Attend your scheduled viewing on time.', 'Ask questions and view the property thoroughly.']
            },
            {
              number: 5,
              title: 'Check Viewings',
              description: ['Click on the \'My Viewings\' button to see your scheduled viewings.', 'Attend viewings on time.']
            },
            {
              number: 6,
              title: 'Stay Updated',
              description: ['Regularly check the \'Keep Me Informed\' button for updates.', 'We will notify you of any property updates.']
            }
          ]
        };

      case 3: // property_assigned
        return {
          messageHeader: '',
          messageTitle: 'Property Assigned',
          messageDescription: 'Thank you for choosing the property and attending a viewing. We will contact you shortly to advise about the next steps.',
          showResources: true,
          showAvailableSpaces: true,
          showNextSteps: false,
          showPropertyAssignedMessage: true
        };

      case 4: // payment_pending
        const paymentConfirmed = latestRent?.payment_confirmed;
        // Laravel: payment_pending.blade.php shows "Property Assignment: {property name} ({property_id})" as title
        const propertyName = latestRent?.room?.property?.name || 'Property';
        const propertyId = latestRent?.room?.property?.property_id || 'ID';
        return {
          messageHeader: '',
          messageTitle: paymentConfirmed 
            ? 'Awaiting Key Collection' 
            : `Property Assignment: ${propertyName} (${propertyId})`, // Laravel: matches payment_pending.blade.php line 24
          messageDescription: paymentConfirmed 
            ? 'Congratulations! Your payment has been processed. You are now in the final stage of your guardian placement. Please use the "Have a Query" button at the top left of the page to arrange when and where to collect your keys. If you have any questions, use the "Have a Query" button at the top left of the page.'
            : 'Please submit payment in full and monitor our system for updates, as it will automatically reflect when your payment has been processed. Follow the steps to review your licence agreement, note your property address, use your Guardian ID for payments, and confirm your payment by clicking "Payment Submitted". For questions, use the "Have a Query" button at the top left of the page.',
          showResources: true,
          showAvailableSpaces: false, // Laravel: payment_pending.blade.php does NOT include _available_spaces
          showNextSteps: false,
          paymentConfirmed
        };

      case 5: // awaiting_key_collection
        const licenceDateSigned = latestRent?.licence_date_signed;
        if (licenceDateSigned) {
          // Laravel: final-message.blade.php "You're Almost Home!" - shown after signing license
          // Note: Laravel final-message.blade.php does NOT include _resources section
          return {
            messageHeader: '',
            messageTitle: "You're Almost Home!",
            messageDescription: `Thank you for signing your licence agreement. You're now just one step away from moving into your new home!
              <br><br>To complete your move-in process, please follow these next steps:
              <br><br>
              <ol style="text-align: left; padding-left: 20px; margin: 0;">
                <li><strong>Key Collection:</strong> In the form below, type the date and time you can come to the office to collect your keys in the Description box and then press the Submit new ticket button. Our Guardian Care Team will then contact you shortly to arrange for you to collect your keys. You can check the status of your request in the <a href="/guardian-tickets" style="color: #fff; font-weight: bold;">My Tickets section</a></li>
                <li><strong>Move-In:</strong> Once you have your keys, you can move into your chosen property. Your guardianship officially begins when our Guardian Care Team starts this on My Globe.</li>
              </ol>
              <br>
              <strong>We are excited to welcome you to your new home with Global Guardians. If you have any questions or need assistance, please don't hesitate to reach out to our team.</strong>
              <br><br><strong>Welcome to the community!</strong>`,
            showResources: false, // Laravel final-message.blade.php does NOT show resources section
            showAvailableSpaces: false, // Don't show available spaces, show key collection form instead
            showNextSteps: false,
            isFinalMessage: true,
            licenceDateSigned: true
          };
        } else {
          // Laravel: sign_agreement.blade.php "Final Step to Secure Your New Home!"
          // Note: Laravel does NOT show resources section or progress stepper for this status
          return {
            messageHeader: '',
            messageTitle: 'Final Step to Secure Your New Home!',
            messageDescription: `Congratulations on reaching the final step of your journey to securing your chosen property with Global Guardians! To complete your move-in process, please follow these simple steps:
              <br><br>
              <ol style="text-align: left; padding-left: 20px; margin: 0;">
                <li>Scroll Down: Carefully review the licence agreement. Ensure you understand all the terms and conditions.</li>
                <li>Sign in the Yellow Box: Once you have reviewed the agreement, scroll down to the yellow box at the bottom, and sign within the yellow box.</li>
                <li>Submit Documentation: Click the button labeled "Submit documentation" to finalise your agreement.</li>
              </ol>
              <br>
              <strong>By signing this agreement, you're one step closer to moving into your new home. Don't wait any longer – complete this final step now!</strong>`,
            showResources: false, // Laravel sign_agreement.blade.php does NOT show resources section
            showAvailableSpaces: false, // Don't show available spaces, show signature form instead
            showNextSteps: false,
            requiresSignature: true,
            licenceDateSigned: false,
            hideProgressStepper: true // Laravel does NOT show progress stepper for awaiting key collection
          };
        }

      case 6: // placed
        return {
          messageHeader: '',
          messageTitle: 'Welcome to Global Guardians',
          messageDescription: 'Thank you for being a property guardian with Global Guardians. Please log in to My Globe regularly to check your documents and maintenance tickets. We will be developing additional features as we progress the My Globe platform.<br><br>Any feedback you have please send through to guardiancare@global-guardians.co.uk',
          showResources: true,
          showAvailableSpaces: true,
          showNextSteps: false,
          showWelcomePack: true // Laravel: only shows Welcome Pack link when isPlaced() is true (status 6)
        };

      case 10: // ntq (Laravel treats NTQ same as placed - shows Welcome message but NO Welcome Pack link)
      case 16: // active
        return {
          messageHeader: '',
          messageTitle: 'Welcome to Global Guardians',
          messageDescription: 'Thank you for being a property guardian with Global Guardians. Please log in to My Globe regularly to check your documents and maintenance tickets. We will be developing additional features as we progress the My Globe platform.<br><br>Any feedback you have please send through to guardiancare@global-guardians.co.uk',
          showResources: true,
          showAvailableSpaces: true,
          showNextSteps: false,
          showWelcomePack: false // Laravel: isPlaced() returns false for NTQ and active, so no Welcome Pack link
        };

      case 8: // blacklisted
      case 18: // blacklisted_violations
        return {
          messageHeader: '',
          messageTitle: 'Your account is blacklisted',
          messageDescription: 'You are no longer able to apply to become a property guardian with us. If you do have any queries, please email: guardiancare@global-guardians.co.uk',
          showResources: false,
          showAvailableSpaces: false,
          showNextSteps: false
        };

      case 9: // not_interested / declined
        return {
          messageHeader: 'My messages',
          messageTitle: `Sorry ${firstName}!`,
          messageDescription: 'Your request has been declined.',
          showResources: false,
          showAvailableSpaces: false,
          showNextSteps: false
        };

      default:
        console.log('⚠️ [DASHBOARD] Unknown status_id:', statusId, 'using default content');
        return {
          messageHeader: '',
          messageTitle: 'Guardian Dashboard',
          messageDescription: 'Welcome to your guardian dashboard.',
          showResources: true,
          showAvailableSpaces: false,
          showNextSteps: false
        };
    }
  };

  const loadStage1Requirements = async () => {
    try {
      const res = await guardianDashboardService.getStageOneRequirements();
      setStage1Reqs(res.data || []);
    } catch (err) {
      console.error('Stage 1 requirements error:', err);
    }
  };

  useEffect(() => {
    // Load stage 1 requirements when in stage 1 (matching Laravel logic)
    if (applicationStage?.current === 'stage_1') {
      loadStage1Requirements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationStage?.current]);

  // Load employment history when dashboard data is available
  useEffect(() => {
    if (
      dashboardData?.guardian?.id &&
      applicationStage?.current === 'stage_1'
    ) {
      loadEmploymentHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData?.guardian?.id, applicationStage?.current]);

  // Update employment history validation when it changes
  useEffect(() => {
    if (employmentHistory.length > 0) {
      // This will trigger re-render of validation status
    }
  }, [employmentHistory]);

  // Document upload functions - following Laravel logic exactly
  const handleDocumentUpload = async (file, documentType) => {
    try {
      setUploadingDocuments(true);
      setDocumentUploadProgress(0);

      // Upload document to S3 (matching Laravel's document storage approach)
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', documentType);
      formData.append('guardian_id', dashboardData.guardian.id);

      const response = await guardianDashboardService.uploadDocument(
        formData,
        (progress) => {
          setDocumentUploadProgress(progress);
        }
      );

      // Update local state with uploaded document info
      setStage1Docs((prev) => ({
        ...prev,
        [documentType]: {
          key: response.document_key,
          filename: file.name,
          uploaded_at: new Date().toISOString(),
          status: 'uploaded',
        },
      }));

      toast.success(`${documentType} uploaded successfully!`);

      // Check if all required documents are uploaded to progress to next stage
      await checkStage1Completion();
    } catch (error) {
      console.error('Document upload error:', error);
      toast.error(`Failed to upload ${documentType}: ${error.message}`);
    } finally {
      setUploadingDocuments(false);
      setDocumentUploadProgress(0);
    }
  };

  const checkStage1Completion = async () => {
    try {
      // Check if all required documents are uploaded (matching Laravel validation)
      const requiredDocs = stage1Reqs.map((req) => req.document_type);
      const uploadedDocs = Object.keys(stage1Docs);

      const allUploaded = requiredDocs.every(
        (docType) =>
          uploadedDocs.includes(docType) &&
          stage1Docs[docType].status === 'uploaded'
      );

      if (allUploaded) {
        // Update guardian status to documents_uploaded (status_id: 11)
        await guardianDashboardService.updateGuardianStatus(11); // documents_uploaded

        // Update local stage state
        setCurrentStage((prev) => ({
          ...prev,
          active: 'stage_3', // Stage 2: Property Search (Laravel: stage_3 means Stage 2 is active)
          sent: true,
        }));

        toast.success('Stage 1 completed! Moving to Stage 2: Property Search');

        // Refresh dashboard data to get updated status
        await fetchDashboardData();
      }
    } catch (error) {
      console.error('Stage 1 completion check error:', error);
      toast.error('Failed to check stage completion');
    }
  };

  const handleStage1Submit = async () => {
    try {
      setSubmittingStage1(true);

      // Validate all required fields (matching Laravel validation rules)
      const errors = {};

      if (!profilePicKey)
        errors.profile_picture = 'Profile picture is required';
      if (!heardAbout)
        errors.heard_about = 'How you heard about us is required';
      if (!dateOfBirth) errors.date_of_birth = 'Date of birth is required';
      if (criminalRecord === null)
        errors.criminal_record = 'Criminal record declaration is required';
      if (medicalCondition === null)
        errors.medical_condition = 'Medical condition declaration is required';
      if (!nextOfKin)
        errors.next_of_kin = 'Next of kin information is required';

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        toast.error('Please complete all required fields');
        return;
      }

      // Save stage 1 data to backend (matching Laravel's stage1Data structure)
      const stage1Data = {
        profile_picture: profilePicKey,
        heard_about: heardAbout,
        date_of_birth: dateOfBirth,
        criminal_record: criminalRecord ? 1 : 0,
        medical_condition: medicalCondition ? 1 : 0,
        next_of_kin: nextOfKin,
        documents: stage1Docs,
        completed_at: new Date().toISOString(),
      };

      await guardianDashboardService.saveStage1Data(stage1Data);

      // Check if documents are also complete
      await checkStage1Completion();

      toast.success('Stage 1 data saved successfully!');
    } catch (error) {
      console.error('Stage 1 submission error:', error);
      toast.error('Failed to save stage 1 data');
    } finally {
      setSubmittingStage1(false);
    }
  };

  // Phone number validation function - only allow numbers, spaces, hyphens, plus, parentheses
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    // Laravel equivalent: UK/International phone number format validation
    const phoneRegex = /^[0-9+\-\s()]*$/;
    
    // Check if value is valid format
    if (phoneRegex.test(value) || value === '') {
      // Check maximum length (20 chars including formatting, max 15 digits)
      const cleanPhone = value.replace(/[^0-9]/g, '');
      
      if (value.length <= 20 && cleanPhone.length <= 15) {
        setRecommendForm({
          ...recommendForm,
          phone_number: value,
        });
      }
      // If too long, don't update the field (prevents typing beyond limit)
    }
    // If invalid characters entered, don't update the field (prevents typing)
  };

  const handleRecommendSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Client-side phone number validation before submission
    const cleanPhone = recommendForm.phone_number.replace(/[^0-9]/g, ''); // Remove non-numeric chars for length check
    if (cleanPhone.length < 10) {
      toast.error('Please enter a valid phone number (minimum 10 digits)');
      setSubmitting(false);
      return;
    }
    
    if (cleanPhone.length > 15) {
      toast.error('Please enter a valid phone number (maximum 15 digits)');
      setSubmitting(false);
      return;
    }

    try {
      const result = await guardianDashboardService.recommendFriend(
        recommendForm
      );
      
      // Show success message (with warning if email not configured)
      if (result.warning) {
        toast.warning(result.message);
        console.warn('Email service warning:', result.warning);
      } else {
        toast.success(result.message);
      }
      
      // Clear form
      setRecommendForm({ name: '', phone_number: '', email: '' });
      
      // Laravel equivalent: redirect()->route('guardians.recommend-a-friend.thanks')
      if (result.redirect) {
        // Redirect to thank you page after a short delay to show the success message
        setTimeout(() => {
          window.location.href = result.redirect;
        }, 1500);
      }
      
    } catch (error) {
      console.error('Error submitting recommendation:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletterChange = async (checked) => {
    try {
      // Get guardian ID from dashboard data (more reliable than user object)
      const guardianId = dashboardData?.guardian?.id;

      if (!guardianId) {
        console.error(
          'Guardian ID not found in dashboard data:',
          dashboardData
        );
        toast.error(
          'Guardian ID not found. Please refresh the page or contact support.'
        );
        return;
      }

      console.log(
        'GuardianDashboard - Using guardian ID from dashboard:',
        guardianId
      );

      const result = await guardianDashboardService.updateNewsletter(
        checked,
        guardianId
      );
      setNewsletterChecked(checked);
      toast.success(result.message);
    } catch (error) {
      console.error('Error updating newsletter:', error);
      toast.error(error.message);
    }
  };

  const handleVoluntaryWorkChange = async (checked) => {
    try {
      // Get guardian ID from dashboard data (more reliable than user object)
      const guardianId = dashboardData?.guardian?.id;

      if (!guardianId) {
        console.error(
          'Guardian ID not found in dashboard data:',
          dashboardData
        );
        toast.error(
          'Guardian ID not found. Please refresh the page or contact support.'
        );
        return;
      }

      console.log(
        'GuardianDashboard - Using guardian ID from dashboard:',
        guardianId
      );

      const result = await guardianDashboardService.updateVoluntaryWork(
        checked,
        guardianId
      );
      setVoluntaryWorkChecked(checked);
      toast.success(result.message);
    } catch (error) {
      console.error('Error updating voluntary work:', error);
      toast.error(error.message);
    }
  };

  const handleEmptyPropertyTip = async () => {
    navigate('/guardians/empty-property-tip');
  };

  const latestRent = dashboardData?.latestRent;

  // ✅ SIGNATURE CANVAS FUNCTIONALITY - Interactive HTML5 Canvas
  const canvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);
  const hasDrawnRef = React.useRef(false);
  const [signatureData, setSignatureData] = React.useState('');

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  };

  const getCanvasCoords = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const { x, y } = getCanvasCoords(e.clientX, e.clientY);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const { x, y } = getCanvasCoords(e.clientX, e.clientY);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawnRef.current = true;
  };

  const stopDrawing = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;

    // Only capture signature data if user has actually drawn something
    if (hasDrawnRef.current && canvasRef.current) {
      const dataURL = canvasRef.current.toDataURL();
      setSignatureData(dataURL);
    }
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(touch.clientX, touch.clientY);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleTouchMove = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(touch.clientX, touch.clientY);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawnRef.current = true;
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    setSignatureData('');
  };

  React.useEffect(() => {
    initializeCanvas();
  }, []);

  // ✅ ENHANCED STAGE 3 HANDLERS - Laravel-compatible API calls

  const handleStage3PaymentSubmit = async () => {
    if (!latestRent?.id) {
      toast.error('Rent information not found');
      return;
    }
    setSubmittingPayment(true);
    try {
      // Use enhanced payment confirmation API
      await guardianDashboardService.submitPaymentConfirmation(latestRent.id);
      toast.success('Payment confirmation submitted successfully!');
      // Refresh dashboard to reflect updated status/payment
      await fetchDashboardData();
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error(error.message || 'Failed to submit payment');
    } finally {
      setSubmittingPayment(false);
    }
  };


  // Handle license agreement signature submission
  const handleLicenseSignatureSubmit = async () => {
    if (!latestRent?.id) {
      toast.error('Rent information not found');
      return;
    }

    // Validate signature
    if (!signatureData) {
      toast.error('Please provide your signature');
      return;
    }

    // Validate signature date (uses state)
    if (!signatureDate) {
      toast.error('Please enter signature date');
      return;
    }

    try {
      setSubmittingPayment(true); // Reuse loading state
      const response = await guardianDashboardService.submitLicenseSignature(latestRent.id, {
        signature: signatureData, // Real canvas signature data
        dateSigned: signatureDate, // From state
      });
      
      toast.success(response.message || 'License agreement signed successfully!');
      
      // Clear signature after successful submission
      clearSignature();
      
      await fetchDashboardData(); // Refresh to show completion
    } catch (error) {
      console.error('Error submitting signature:', error);
      toast.error(error.message || 'Failed to submit signature');
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Handle license agreement download
  const handleLicenseDownload = async () => {
    if (!latestRent?.id) {
      toast.error('Rent information not found');
      return;
    }

    try {
      const blob = await guardianDashboardService.getLicenseAgreement(latestRent.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `license_agreement_${latestRent.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('License agreement downloaded');
    } catch (error) {
      console.error('Error downloading license agreement:', error);
      toast.error(error.message || 'Failed to download license agreement');
    }
  };

  // Handle key collection ticket submission (Laravel: final-message.blade.php -> tickets.store)
  const handleKeyCollectionSubmit = async (e) => {
    e.preventDefault();

    if (!keyCollectionDescription.trim()) {
      toast.error('Please enter the date and time you would like to collect the keys');
      return;
    }

    const latestRent = dashboardData?.latestRent;
    if (!latestRent?.room?.property?.id || !latestRent?.room?.id) {
      toast.error('Property or room information not found');
      return;
    }

    try {
      setSubmittingKeyCollection(true);
      await guardianDashboardService.submitKeyCollectionTicket({
        description: keyCollectionDescription,
        propertyId: latestRent.room.property.id,
        roomId: latestRent.room.id,
      });

      toast.success('Key collection request submitted successfully! Our Guardian Care Team will contact you shortly.');
      setKeyCollectionDescription(''); // Clear the form
    } catch (error) {
      console.error('Error submitting key collection ticket:', error);
      toast.error(error.message || 'Failed to submit key collection request');
    } finally {
      setSubmittingKeyCollection(false);
    }
  };

  // Handle welcome pack download
  const handleWelcomePackDownload = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/property-application/file/welcome_kit`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Authentication required. Please log in again.');
          return;
        } else if (response.status === 404) {
          toast.error('Welcome pack not found.');
          return;
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Welcome_Kit.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Welcome pack downloaded successfully');
    } catch (error) {
      console.error('Error downloading welcome pack:', error);
      toast.error(error.message || 'Failed to download welcome pack');
    }
  };

  const handleStage1Upload = (key) => (s3Key, fileName) => {
    setStage1Docs((prev) => ({
      ...prev,
      [key]: {
        key: s3Key,
        name: fileName || `Document ${key}`,
        uploadedAt: new Date().toISOString(),
      },
    }));
  };

  const handleProfileUpload = (s3Key, fileName) => {
    console.log('[GUARDIAN DASHBOARD] handleProfileUpload called:', { s3Key, fileName });
    setProfilePicKey({
      key: s3Key,
      name: fileName || 'Profile Picture',
      uploadedAt: new Date().toISOString(),
    });
    setHasUserInteracted(true); // ✅ Mark user interaction
  };

  const handleRemoveStage1File = (key) => {
    setStage1Docs((prev) => {
      const newDocs = { ...prev };
      delete newDocs[key];
      return newDocs;
    });
  };

  const handleRemoveProfilePic = () => {
    setProfilePicKey('');
    setProfilePreviewUrl(null);
  };

  // Employment history functions
  const loadEmploymentHistory = async () => {
    if (!dashboardData?.guardian?.id) return;

    setLoadingEmployment(true);
    try {
      console.log(
        '🔄 [FRONTEND] Loading employment history for guardian ID:',
        dashboardData.guardian.id
      );
      const response = await employmentHistoryService.getByGuardianId(
        dashboardData.guardian.id
      );
      console.log('📊 [FRONTEND] Employment history response:', response);
      console.log(
        '📋 [FRONTEND] Setting employment history to:',
        response || []
      );
      // Fix: response is already the data array (service returns response.data.data)
      setEmploymentHistory(response || []);
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading employment history:', error);
      toast.error('Failed to load employment history');
    } finally {
      setLoadingEmployment(false);
    }
  };

  // ✅ ENHANCED: Laravel BS8584 compliant employment history validation with error handling
  const validateEmploymentHistory = () => {
    try {
      if (employmentHistory.length === 0) {
        console.log('🔍 [VALIDATION] No employment history found');
        return {
          isValid: false,
          message: 'At least one employment record is required',
        };
      }

      console.log('🔍 [VALIDATION] Validating employment history:', employmentHistory);

      // ✅ Pre-validation: Check for incomplete employment records
      const incompleteRecords = employmentHistory.filter(record => 
        !record.company_name || 
        !record.date_start || 
        (!record.still_in_employment && !record.date_end) ||
        (record.still_in_employment !== '1' && record.still_in_employment !== 1 && !record.date_end)
      );

      if (incompleteRecords.length > 0) {
        console.log('❌ [VALIDATION] Found incomplete employment records:', incompleteRecords);
        return {
          isValid: false,
          message: `Please complete all employment record fields. Found ${incompleteRecords.length} incomplete record(s). Each employment record must have a company name, start date, and either "Still in Employment" set to Yes or an end date.`,
        };
      }

      // ✅ Laravel BS8584 requirement: Check 2-year employment history coverage
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      
      // Sort employment records by start date
      const sortedHistory = [...employmentHistory].sort((a, b) => {
        const dateA = new Date(a.date_start);
        const dateB = new Date(b.date_start);
        return dateA - dateB;
      });

      console.log('🔍 [VALIDATION] Sorted employment history:', sortedHistory);
      console.log('🔍 [VALIDATION] Coverage period required:', {
        from: twoYearsAgo.toLocaleDateString(),
        to: new Date().toLocaleDateString()
      });

      // Check for gaps in employment history
      let currentDate = twoYearsAgo;
      let hasCoverage = true;
      let gapMessages = [];

      for (const record of sortedHistory) {
        // Validate dates exist and are valid
        if (!record.date_start) {
          console.warn('⚠️ [VALIDATION] Employment record missing start date:', record);
          continue;
        }

        const startDate = new Date(record.date_start);
        if (isNaN(startDate.getTime())) {
          console.warn('⚠️ [VALIDATION] Invalid start date:', record.date_start);
          continue;
        }

        // Determine end date - handle still employed case
        let endDate;
        if (record.still_in_employment === '1' || record.still_in_employment === 1) {
          endDate = new Date(); // Current employment
        } else if (record.date_end) {
          endDate = new Date(record.date_end);
          if (isNaN(endDate.getTime())) {
            console.warn('⚠️ [VALIDATION] Invalid end date:', record.date_end);
            endDate = new Date(); // Assume current if invalid
          }
        } else {
          console.warn('⚠️ [VALIDATION] Missing end date for completed employment:', record);
          endDate = startDate; // Assume same day if missing
        }

        // Check if there's a gap before this employment
        if (startDate > currentDate) {
          const gapDays = Math.floor((startDate - currentDate) / (1000 * 60 * 60 * 24));
          if (gapDays > 30) { // Allow 30-day gaps
            hasCoverage = false;
            gapMessages.push(`Gap of ${gapDays} days before ${record.company_name || 'Unknown Company'}`);
          }
        }

        // Update current date to end of this employment
        currentDate = new Date(Math.max(currentDate, endDate));
      }

      // Check if coverage extends to present day
      const today = new Date();
      const daysSinceLastEmployment = Math.floor((today - currentDate) / (1000 * 60 * 60 * 24));
      
      if (daysSinceLastEmployment > 30) {
        hasCoverage = false;
        gapMessages.push(`${daysSinceLastEmployment} days without employment until present`);
      }

      if (!hasCoverage) {
        // Create user-friendly error message
        let userFriendlyMessage = 'Employment history must cover the past 2 years continuously (BS8584 requirement). ';
        
        if (gapMessages.length === 1) {
          userFriendlyMessage += `Issue found: ${gapMessages[0]}.`;
        } else if (gapMessages.length > 1) {
          userFriendlyMessage += `Issues found: ${gapMessages.slice(0, 2).join(', ')}`;
          if (gapMessages.length > 2) {
            userFriendlyMessage += ` and ${gapMessages.length - 2} more issue(s).`;
          } else {
            userFriendlyMessage += '.';
          }
        }
        
        userFriendlyMessage += ' Please add missing employment records or adjust dates to ensure continuous coverage.';
        
        console.log('🔍 [VALIDATION] Employment coverage failed:', userFriendlyMessage);
        console.log('🔍 [VALIDATION] Detailed gap analysis:', gapMessages);
        
        return {
          isValid: false,
          message: userFriendlyMessage,
        };
      }

      console.log('✅ [VALIDATION] Employment history validation passed');
      console.log('✅ [VALIDATION] Coverage summary:', {
        totalRecords: employmentHistory.length,
        sortedRecords: sortedHistory.length,
        coverageFrom: twoYearsAgo.toLocaleDateString(),
        coverageTo: new Date().toLocaleDateString(),
        lastEmploymentEnd: currentDate.toLocaleDateString()
      });
      
      return {
        isValid: true,
        message: 'Employment history meets BS8584 requirements',
      };
    } catch (error) {
      console.error('❌ [VALIDATION] Error in employment history validation:', error);
      // If validation fails due to error, allow submission but log the issue
      return {
        isValid: true,
        message: 'Employment history validation completed with warnings',
      };
    }
  };

  const handleEmploymentFormChange = (field, value) => {
    setEmploymentForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // ✅ Real-time validation: Clear field error when user starts typing
    if (employmentFormErrors[field]) {
      setEmploymentFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // ✅ LARAVEL VALIDATION PARITY: Employment form validation exactly matching Laravel rules
  const validateEmploymentForm = () => {
    const errors = {};

    // Required fields (matching Laravel validation)
    if (!employmentForm.company_name?.trim()) {
      errors.company_name = 'Company name is required';
    } else if (employmentForm.company_name.length > 255) {
      errors.company_name = 'Company name cannot exceed 255 characters';
    }

    if (!employmentForm.address_1?.trim()) {
      errors.address_1 = 'Address 1 is required';
    } else if (employmentForm.address_1.length > 255) {
      errors.address_1 = 'Address 1 cannot exceed 255 characters';
    }

    if (!employmentForm.town?.trim()) {
      errors.town = 'Town is required';
    } else if (employmentForm.town.length > 255) {
      errors.town = 'Town cannot exceed 255 characters';
    }

    if (!employmentForm.postcode?.trim()) {
      errors.postcode = 'Postcode is required';
    } else if (employmentForm.postcode.length > 255) {
      errors.postcode = 'Postcode cannot exceed 255 characters';
    } else {
      // UK postcode validation pattern (matching Laravel)
      const postcodePattern = /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?\s[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}$/;
      if (!postcodePattern.test(employmentForm.postcode.trim())) {
        errors.postcode = 'Please enter a valid UK postcode (e.g., SW1A 1AA)';
      }
    }

    if (!employmentForm.job_title?.trim()) {
      errors.job_title = 'Job title is required';
    } else if (employmentForm.job_title.length > 255) {
      errors.job_title = 'Job title cannot exceed 255 characters';
    }

    // Date validation (conditional, matching Laravel logic)
    if (!employmentForm.date_start?.trim()) {
      errors.date_start = 'Start date is required';
    } else {
      // Convert to d/m/Y format for Laravel compatibility
      const startDate = new Date(employmentForm.date_start);
      if (isNaN(startDate.getTime())) {
        errors.date_start = 'Please enter a valid start date';
      }
    }

    // Conditional date validation based on still_in_employment
    if (employmentForm.still_in_employment === '1') {
      // Still employed - end date should be empty or not required
      // (matching Laravel logic)
    } else {
      // Not employed - end date is required and must be after start date
      if (!employmentForm.date_end?.trim()) {
        errors.date_end = 'End date is required when not still employed';
      } else {
        const endDate = new Date(employmentForm.date_end);
        const startDate = new Date(employmentForm.date_start);
        
        if (isNaN(endDate.getTime())) {
          errors.date_end = 'Please enter a valid end date';
        } else if (employmentForm.date_start && endDate <= startDate) {
          errors.date_end = 'End date must be after start date';
        }
      }
    }

    // Optional fields with max length validation
    ['address_2', 'address_3', 'referee_name', 'referee_telephone', 'referee_email', 'reason_for_leaving'].forEach(field => {
      if (employmentForm[field] && employmentForm[field].length > 255) {
        errors[field] = `${field.replace(/_/g, ' ')} cannot exceed 255 characters`;
      }
    });

    console.log('🔍 [EMPLOYMENT FORM] Validation result:', {
      hasErrors: Object.keys(errors).length > 0,
      errors: errors,
      formData: employmentForm
    });

    return errors;
  };

  const resetEmploymentForm = () => {
    setEmploymentForm({
      date_start: '',
      date_end: '',
      still_in_employment: '0',
      company_name: '',
      address_1: '',
      address_2: '',
      address_3: '',
      town: '',
      postcode: '',
      referee_name: '',
      referee_telephone: '',
      referee_email: '',
      job_title: '',
      reason_for_leaving: '',
    });
    setEditingEmploymentId(null);
    setEmploymentFormErrors({}); // ✅ Clear form errors when resetting
    setShowEmploymentForm(false);
  };

  const handleEmploymentSubmit = async (e) => {
    e.preventDefault();
    setSubmittingEmployment(true);

    try {
      // ✅ LARAVEL VALIDATION PARITY: Validate form before submission
      const validationErrors = validateEmploymentForm();
      setEmploymentFormErrors(validationErrors);
      
      if (Object.keys(validationErrors).length > 0) {
        console.log('❌ [EMPLOYMENT FORM] Validation failed:', validationErrors);
        toast.error('Please fix the form errors before submitting');
        setSubmittingEmployment(false);
        return;
      }

      if (editingEmploymentId) {
        await employmentHistoryService.update(
          editingEmploymentId,
          employmentForm
        );
        toast.success('Employment history updated successfully');
      } else {
        await employmentHistoryService.create(employmentForm);
        toast.success('Employment history added successfully');
      }

      resetEmploymentForm();
      await loadEmploymentHistory();
    } catch (error) {
      console.error('Error saving employment history:', error);
      toast.error(error.message || 'Failed to save employment history');
    } finally {
      setSubmittingEmployment(false);
    }
  };

  const handleEditEmployment = (record) => {
    setEmploymentForm({
      date_start: record.date_start
        ? new Date(record.date_start).toISOString().split('T')[0]
        : '',
      date_end: record.date_end
        ? new Date(record.date_end).toISOString().split('T')[0]
        : '',
      still_in_employment: record.still_in_employment ? '1' : '0',
      company_name: record.company_name || '',
      address_1: record.address_1 || '',
      address_2: record.address_2 || '',
      address_3: record.address_3 || '',
      town: record.town || '',
      postcode: record.postcode || '',
      referee_name: record.referee_name || '',
      referee_telephone: record.referee_telephone || '',
      referee_email: record.referee_email || '',
      job_title: record.job_title || '',
      reason_for_leaving: record.reason_for_leaving || '',
    });
    setEditingEmploymentId(record.id);
    setShowEmploymentForm(true);
  };

  const handleDeleteEmployment = (id) => {
    setDeletingEmploymentId(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteEmployment = async () => {
    if (!deletingEmploymentId) return;

    try {
      // Set deletion state when API call starts
      setIsDeleting(true);

      console.log(
        '🗑️ [FRONTEND] Deleting employment record with ID:',
        deletingEmploymentId
      );
      const response = await employmentHistoryService.delete(
        deletingEmploymentId
      );
      console.log('✅ [FRONTEND] Delete response:', response);
      toast.success('Employment history deleted successfully');

      // Immediately remove the record from state for better UX
      setEmploymentHistory((prev) =>
        prev.filter((record) => record.id !== deletingEmploymentId)
      );

      // Force refresh employment history to ensure sync with backend
      console.log('🔄 [FRONTEND] Reloading employment history...');
      await loadEmploymentHistory();

      setShowDeleteModal(false);
      setDeletingEmploymentId(null);
      setIsDeleting(false);
    } catch (error) {
      console.error('❌ [FRONTEND] Error deleting employment history:', error);
      toast.error(error.message || 'Failed to delete employment history');
      setIsDeleting(false);
    }
  };

  const cancelDeleteEmployment = () => {
    setShowDeleteModal(false);
    setDeletingEmploymentId(null);
    setIsDeleting(false);
  };

  // Clean up deleting state when modal is closed
  const handleCloseDeleteModal = () => {
    if (deletingEmploymentId) {
      setDeletingEmploymentId(null);
    }
    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  const handleShowEmployment = (record) => {
    setSelectedEmployment(record);
    setShowViewModal(true);
  };

  // Real-time validation function
  const validateForm = () => {
    const errors = {};

    // ✅ FIXED: Laravel-compatible date of birth validation
    if (!dateOfBirth || dateOfBirth.trim() === '') {
      errors.dateOfBirth = 'Date of Birth is required';
    } else {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      
      // Check if date is valid
      if (isNaN(birthDate.getTime())) {
        errors.dateOfBirth = 'Please enter a valid date';
      } else if (birthDate >= today) {
        // ✅ CRITICAL FIX: Check for future dates first
        errors.dateOfBirth = 'Date of birth cannot be in the future';
      } else {
        // ✅ FIXED: Corrected age calculation logic
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        // Adjust age if birthday hasn't occurred this year
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
          age--;
        }

        if (age < 18 || age > 100) {
          errors.dateOfBirth = 'Age must be between 18-100 years';
        }
      }
    }

    // Validate criminal record
    if (criminalRecord === null || criminalRecord === undefined) {
      errors.criminalRecord =
        'Please select whether you have a criminal record';
    } else if (criminalRecord === true && (!criminalRecordDescription || criminalRecordDescription.trim() === '')) {
      // ✅ NEW: Laravel-compatible conditional validation
      errors.criminalRecordDescription = 'Please provide details of your criminal record';
    }

    // Validate medical condition
    if (medicalCondition === null || medicalCondition === undefined) {
      errors.medicalCondition =
        'Please select whether you have a medical condition';
    } else if (medicalCondition === true && (!medicalConditionDescription || medicalConditionDescription.trim() === '')) {
      // ✅ NEW: Laravel-compatible conditional validation
      errors.medicalConditionDescription = 'Please provide details of your medical condition';
    }

    // ✅ LARAVEL-EXACT: Validate next of kin (simple validation matching Laravel)
    if (!nextOfKin || nextOfKin.trim() === '') {
      errors.nextOfKin = 'Next of kin details are required';
    }

    // Validate profile picture (required)
    if (!profilePicKey || !profilePicKey.key) {
      errors.profilePicture = 'Profile picture is required';
    }

    // Validate employment history (at least one record required)
    if (employmentHistory.length === 0) {
      errors.employmentHistory = 'At least one employment record is required';
    }

    // Validate required documents
    const requiredKeys = [
      'passport',
      'bank_statement_1',
      'bank_statement_2',
      'bank_statement_3',
      'employment_reference',
      'payslip_1',
      'payslip_2',
      'payslip_3',
    ];

    requiredKeys.forEach((key) => {
      if (!stage1Docs[key] || !stage1Docs[key].key) {
        errors[key] = `${key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
      }
    });

    setValidationErrors(errors);
    setIsFormValid(Object.keys(errors).length === 0);

    return Object.keys(errors).length === 0;
  };

  // ✅ BETTER FIX: Only validate after user has attempted to submit
  useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAttemptedSubmit,
    dateOfBirth,
    criminalRecord,
    criminalRecordDescription,
    medicalCondition,
    medicalConditionDescription,
    nextOfKin,
    heardAbout,
    profilePicKey,
    stage1Docs,
    employmentHistory,
  ]);

  const handleSubmitStage1 = async () => {
    console.log('🚀 [SUBMIT] Starting Stage 1 submission');
    console.log('🚀 [SUBMIT] stage1Reqs:', stage1Reqs);
    
    // ✅ Mark that user has attempted to submit - this will trigger validation
    setHasAttemptedSubmit(true);
    
    // ✅ Run validation first - if invalid, stop here
    const isValid = validateForm();
    if (!isValid) {
      console.log('❌ [SUBMIT] Form validation failed, stopping submission');
      setSubmittingStage1(false);
      return;
    }
    
    if (!stage1Reqs.length) {
      console.warn('⚠️ [SUBMIT] No stage1Reqs found, returning early');
      return;
    }

    console.log('🔍 [SUBMIT] Starting employment history validation');
    
    // ✅ FULL BS8584 VALIDATION RE-ENABLED with enhanced error handling
    try {
      const employmentValidation = validateEmploymentHistory();
      console.log('🔍 [SUBMIT] Employment validation result:', employmentValidation);
      
      if (!employmentValidation.isValid) {
        console.log('❌ [SUBMIT] Employment validation failed:', employmentValidation.message);
        toast.error(employmentValidation.message);
        return;
      }
      
      console.log('✅ [SUBMIT] Full BS8584 employment validation passed');
      
    } catch (error) {
      console.error('❌ [SUBMIT] Critical error during employment validation:', error);
      console.error('❌ [SUBMIT] Error stack:', error.stack);
      toast.error('Error validating employment history. Please check your employment records and try again.');
      return;
    }

    console.log('✅ [SUBMIT] Employment validation passed, starting submission');
    setSubmittingStage1(true);

    try {
      const guardianId = dashboardData?.guardian?.id;
      if (!guardianId) {
        toast.error('Guardian ID not found. Please refresh the page.');
        setSubmittingStage1(false);
        return;
      }

      // Prepare documents payload with S3 keys and filenames (matching Laravel media structure)
      const documentsPayload = {};
      Object.keys(stage1Docs).forEach((key) => {
        if (stage1Docs[key] && stage1Docs[key].key) {
          documentsPayload[key] = {
            s3_key: stage1Docs[key].key,
            file_name: stage1Docs[key].name || stage1Docs[key].key.split('/').pop(),
          };
        }
      });

      // Prepare personal details payload (matching Laravel structure)
      const personalDetailsPayload = {
        date_of_birth: dateOfBirth,
        criminal_record: criminalRecord,
        criminal_record_description: criminalRecordDescription, // ✅ NEW
        medical_condition: medicalCondition,
        medical_condition_description: medicalConditionDescription, // ✅ NEW
        next_of_kin: nextOfKin,
        heard_about: heardAbout,
        profile_picture: profilePicKey?.key || null,
      };

      // Submit complete Stage 1 data (matching Laravel's submitStageOneComplete)
      const response = await guardianDashboardService.submitStageOneComplete({
        guardianId,
        documents: documentsPayload,
        personalDetails: personalDetailsPayload,
      });

      if (response.success) {
        toast.success(response.message);

        // ✅ LARAVEL-COMPATIBLE FIX: Let backend determine the stage, don't force Stage 2
        // Clear form data after successful submission
        setStage1Docs({});
        setProfilePicKey('');
        setDateOfBirth('');
        setCriminalRecord(null);
        setCriminalRecordDescription('');
        setMedicalCondition(null);
        setMedicalConditionDescription('');
        setNextOfKin('');
        setHeardAbout('');

        // Refresh dashboard data to get updated stage information from backend
        await fetchDashboardData();
      }
    } catch (error) {
      console.error('Error submitting Stage 1:', error);
      toast.error(
        error.response?.data?.message || 'Failed to submit Stage 1 data'
      );
    } finally {
      setSubmittingStage1(false);
    }
  };

  // Handler functions for Current Property section
  const handleViewProperty = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  const handleViewRoom = (roomId) => {
    navigate(`/rooms/${roomId}/show`);
  };

  const handleViewLicenseAgreement = async (rentId) => {
    try {
      console.log(`[GUARDIAN_DASHBOARD] Attempting to view signed license agreement for rent ${rentId}`);
      
      // Use rentsService to get presigned URL for direct S3 download
      const presignedUrl = await rentsService.getLicenceDocument(rentId, 'signed_license_agreement');
      
      if (!presignedUrl) {
        throw new Error('No presigned URL returned from server');
      }
      
      // Force download instead of opening in new tab (matching Laravel download behavior)
      console.log(`[GUARDIAN_DASHBOARD] Triggering download for signed license agreement`);
      
      // Fetch file as blob to force download (works for cross-origin URLs)
      const response = await fetch(presignedUrl);
      const blob = await response.blob();
      
      // Create blob URL and download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `signed_license_agreement_${rentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      console.log(`[GUARDIAN_DASHBOARD] Successfully downloaded signed license agreement`);
    } catch (error) {
      console.error('[GUARDIAN_DASHBOARD] Error viewing license agreement:', error);
      toast.error('Failed to download license agreement. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className={styles.guardianDashboard}>
        <div className={styles.container}>
          <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.guardianDashboard}>
      {/* Header Section */}
      <section className={styles.header}>
        <div className={styles.container}>
          <div className={styles.breadcrumb}>
            <div className={styles.dashboardTag}>Dashboard</div>
          </div>

          {(() => {
            // ✅ IMPLEMENTATION 1: Use dynamic status-specific content
            const statusContent = getStatusSpecificContent(dashboardData?.guardian, applicationStage, dashboardData?.latestRent);
            
            return (
              <>
                {/* Dynamic message header */}
                {statusContent.messageHeader && (
                  <div className={styles.messageHeader}>{statusContent.messageHeader}</div>
                )}

                {/* Dynamic message title */}
                <h1 className={styles.messageTitle}>
                  {statusContent.messageTitle}
                </h1>

                {/* Dynamic message description - using dangerouslySetInnerHTML to support HTML content like <br> */}
                {statusContent.messageDescription && (
                  <span className={styles.messageDescription}>
                    <span 
                      dangerouslySetInnerHTML={{ __html: statusContent.messageDescription }}
                      onClick={(e) => {
                        // Handle link clicks in message description (e.g., /spaces/available link)
                        if (e.target.tagName === 'A' && e.target.href) {
                          const href = e.target.getAttribute('href');
                          if (href && href.startsWith('/') && !href.startsWith('mailto:')) {
                            e.preventDefault();
                            navigate(href);
                          }
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    {/* Welcome Pack link for placed guardians - matching Laravel placed.blade.php */}
                    {statusContent.showWelcomePack && (
                      <>
                        <br /><br />
                        Download your Welcome Pack <a
                          href="#"
                          onClick={handleWelcomePackDownload}
                          style={{ color: '#fff', textDecoration: 'underline', cursor: 'pointer' }}
                        >here.</a>
                      </>
                    )}
                  </span>
                )}

                {/* Next Steps for vetted status */}
                {statusContent.showNextSteps && statusContent.nextSteps && (
                  <>
                    <h3 className={styles.nextStepsTitle}>Next Steps:</h3>
                    <div className={styles.nextStepsGrid}>
                      {statusContent.nextSteps.map((step, index) => (
                        <div key={index} className={styles.nextStepCard}>
                          <h4>{step.number}. {step.title}</h4>
                          <ul>
                            {step.description.map((desc, descIndex) => (
                              <li key={descIndex}>{desc}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    <div className={styles.querySection}>
                      <h3>Have a Query?</h3>
                      <p>
                        If you have any questions, click on the 'Have a Query' button at the top left-hand corner of the page to contact us.
                      </p>
                    </div>
                  </>
                )}

                {/* Stage 1 specific content */}
                {applicationStage?.current === 'stage_1' && !applicationStage?.sent && (
                  <>
                    <p className={styles.messageDescription}>
                      Global Guardians is a verified provider of Protection by Occupation. We are audited and certified under ISO 9001, ISO 14001 and BS8584 standards. This certification underscores our commitment to quality management, security and environmental responsibility, ensuring that we consistently meet customer expectations and adhere to sustainable practices.
                    </p>

                    {/* ISO Certification Badges */}
                    <div style={{
                      display: 'flex',
                      gap: '20px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '20px 0',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        border: '2px solid #003366',
                        borderRadius: '8px',
                        padding: '15px 20px',
                        backgroundColor: '#f8f9fa',
                        textAlign: 'center',
                        minWidth: '120px'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#003366', fontSize: '18px', marginBottom: '5px' }}>
                          ISO 9001
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>Quality Management</div>
                      </div>

                      <div style={{
                        border: '2px solid #006633',
                        borderRadius: '8px',
                        padding: '15px 20px',
                        backgroundColor: '#f8f9fa',
                        textAlign: 'center',
                        minWidth: '120px'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#006633', fontSize: '18px', marginBottom: '5px' }}>
                          ISO 14001
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>Environmental</div>
                      </div>

                      <div style={{
                        border: '2px solid #990033',
                        borderRadius: '8px',
                        padding: '15px 20px',
                        backgroundColor: '#f8f9fa',
                        textAlign: 'center',
                        minWidth: '120px'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#990033', fontSize: '18px', marginBottom: '5px' }}>
                          BS8584
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>Property Guardian</div>
                      </div>
                    </div>

                    <p className={styles.messageDescription}>
                      In order to become a Global Guardian we require you to provide your employment history and your documents below so our auditors can verify your application.
                    </p>
                  </>
                )}

                {/* Property assigned specific content */}
                {statusContent.showPropertyAssignedMessage && (
                  <div className={styles.stageBox}>
                    You have selected this property. Please wait until we send you an email.
                  </div>
                )}

                {/* Sign agreement specific content */}
                {statusContent.requiresSignature && (
                  <div className={styles.stageBox}>
                    Please sign your license agreement to proceed with your guardian placement.
                  </div>
                )}

                {/* Final message specific content - Laravel: final-message.blade.php */}
                {/* Note: For isFinalMessage, content is already in header messageDescription - no separate stageBox needed */}
              </>
            );
          })()}
        </div>
      </section>

      {/* Current Property Section - Only for Placed (6) or NTQ (10) status */}
      {/* MATCH LARAVEL: placed.blade.php includes _current-property only for isPlaced() || isNTQ() */}
      {/* Previous Guardian (7) does NOT show current property section */}
      {(() => {
        const guardian = dashboardData?.guardian;
        const latestRent = dashboardData?.latestRent;
        const statusId = guardian?.status_id;
        const statusContent = getStatusSpecificContent(guardian, applicationStage, latestRent);
        const isPlacedOrNTQ = statusId === 6 || statusId === 10; // Placed or NTQ
        
        // Hide current property for previous guardian (status 7) - matching Laravel
        if (statusContent.showCurrentProperty === false) {
          return null;
        }
        
        // Only show for Placed or NTQ status (matching Laravel logic)
        if (!isPlacedOrNTQ || !latestRent) {
          return null;
        }

        // Check if user can have tickets (matching Laravel canHaveTickets() check)
        // Laravel logic: checks if user has 'tickets.create' permission AND guardian is in allowed status
        // Allowed statuses: Registered (1), Documents Required (2), Payment Pending (4), 
        // Awaiting Key Collection (5), Placed (6), NTQ (10), Vetted (12)
        let canHaveTickets = false;
        
        if (user?.canHaveTickets && typeof user.canHaveTickets === 'function') {
          // Use backend canHaveTickets() method if available
          canHaveTickets = user.canHaveTickets();
        } else {
          // Fallback: Check guardian status directly (matching Laravel logic)
          // For guardians, check if status is in allowed list
          const allowedStatusIds = [1, 2, 4, 5, 6, 10, 12]; // Registered, Documents Required, Payment Pending, Awaiting Key Collection, Placed, NTQ, Vetted
          canHaveTickets = statusId && allowedStatusIds.includes(statusId);
        }

        return (
          <section className={styles.mainContent}>
            <div className={styles.container}>
              <div id="js-rooms-of-property-effect"></div>
              <div className="row">
                <div className="col-md-12">
                  <div className="panel-title title-standard relative" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 className="mr-auto" style={{ margin: 0 }}>Current property</h3>
                    
                    {/* Ticket buttons - only show if canHaveTickets() */}
                    {/* MATCH LARAVEL: @if ($currentUser->canHaveTickets()) */}
                    {canHaveTickets && (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          className="btn btn-secondary d-print-none"
                          onClick={() => navigate(`/tickets?guardian_id=${guardian?.id}`)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Your tickets
                        </button>
                        <button
                          className="btn btn-secondary d-print-none"
                          onClick={() => navigate('/tickets/create')}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="glyphicon glyphicon-plus"></i> Submit maintenance request
                        </button>
                      </div>
                    )}
                  </div>

                  {/* MATCH LARAVEL: @if($guardian->latest_not_ended_or_failed_rent) */}
                  {latestRent?.room?.property ? (
                    <div className="panel-group" style={{ padding: '20px', border: '1px solid #eee', borderRadius: '4px', backgroundColor: '#fff' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        {/* Property Details Column */}
                        <div style={{ flex: '0 0 auto', minWidth: '300px', marginRight: '30px' }}>
                          {/* Property ID */}
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ width: '120px', textAlign: 'right', color: '#0a7280', fontWeight: 'normal', marginRight: '10px' }}>Property ID:</span>
                            <span style={{ fontWeight: 'bold', color: '#333' }}>{latestRent.room.property.property_id || '-'}</span>
                          </div>
                          {/* Property Name */}
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ width: '120px', textAlign: 'right', color: '#0a7280', fontWeight: 'normal', marginRight: '10px' }}>Property name:</span>
                            <a
                              href={`/properties/${latestRent.room.property.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewProperty(latestRent.room.property.id); }}
                              style={{ color: '#17264b', textDecoration: 'underline' }}
                            >
                              {latestRent.room.property.name || '-'}
                            </a>
                          </div>
                          {/* Property Address */}
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ width: '120px', textAlign: 'right', color: '#0a7280', fontWeight: 'normal', marginRight: '10px' }}>Property address:</span>
                            <span style={{ fontWeight: 'bold', color: '#333' }}>{latestRent.room.property.address_1 || '-'}</span>
                          </div>
                          {/* Postcode */}
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ width: '120px', textAlign: 'right', color: '#0a7280', fontWeight: 'normal', marginRight: '10px' }}>Postcode:</span>
                            <span style={{ fontWeight: 'bold', color: '#333' }}>{latestRent.room.property.postcode || '-'}</span>
                          </div>
                        </div>

                        {/* Room Card Column - MATCH LARAVEL */}
                        {Array.isArray(latestRent.room?.assignedGuardiansRents) && latestRent.room.assignedGuardiansRents.length > 0 && (
                          <>
                            {latestRent.room.assignedGuardiansRents.map((rent) => {
                              const formatDate = (dateString) => {
                                if (!dateString) return '';
                                const date = new Date(dateString);
                                return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                              };
                              const formatCurrency = (amount) => {
                                if (!amount) return '£0';
                                return `£${parseFloat(amount).toFixed(0)}`;
                              };

                              // Get room name from various possible sources
                              const roomName = latestRent.room?.name || rent.room?.name || `Room ${latestRent.room?.id || ''}`;

                              // Check if rent has notice to quit (matching Laravel logic)
                              const hasNoticeToQuit = rent.notice_to_quit && rent.notice_to_quit.date_notice;
                              const noticeDate = hasNoticeToQuit ? rent.notice_to_quit.date_notice : null;
                              
                              // Format notice date for display (matching Laravel format: "03/01/2026 00:00")
                              const formatNoticeDate = (dateString) => {
                                if (!dateString) return '';
                                const date = new Date(dateString);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                const hours = String(date.getHours()).padStart(2, '0');
                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                return `${day}/${month}/${year} ${hours}:${minutes}`;
                              };

                              return (
                                <div key={rent.id} style={{ flex: '0 0 auto', minWidth: '150px', maxWidth: '180px', marginRight: '20px' }}>
                                  {/* MATCH LARAVEL: rooms._room_of_guardian.blade.php */}
                                  <div className="room-information room-occupied" style={{
                                    height: 'auto',
                                    width: '100%',
                                    display: 'block',
                                    backgroundColor: '#850505',
                                    marginBottom: '10px'
                                  }}>
                                    {/* Room Name Header */}
                                    <p className="room-number" style={{
                                      color: '#fff',
                                      fontWeight: 'bold',
                                      fontSize: '14px',
                                      padding: '8px 10px',
                                      margin: 0,
                                      backgroundColor: '#850505'
                                    }}>
                                      {roomName}
                                    </p>

                                    {/* Status - Show NOTICE GIVEN if notice_to_quit exists, otherwise OCCUPIED */}
                                    <span className="status" style={{
                                      width: '100%',
                                      display: 'block',
                                      color: '#F5F5F5',
                                      borderTop: '1px solid #600',
                                      fontSize: '10px',
                                      padding: '3px 10px',
                                      textTransform: 'uppercase',
                                      backgroundColor: '#850505'
                                    }}>
                                      {hasNoticeToQuit ? 'NOTICE GIVEN' : 'OCCUPIED'}
                                    </span>

                                    <ul className="room-information-in room-information-in-display" style={{
                                      width: '100%',
                                      height: 'auto',
                                      backgroundColor: '#ededed', // Laravel: #ededed
                                      padding: 0,
                                      listStyleType: 'none',
                                      borderBottom: '2px solid #e0e0e0',
                                      margin: 0
                                    }}>
                                      <li style={{
                                        fontSize: '80%',
                                        borderTop: 'none',
                                        padding: '5px',
                                        color: '#000'
                                      }}>
                                        <i className="glyphicon glyphicon-user" style={{ color: '#0a7280', margin: '0 10px 0 20px' }}></i>
                                        <a 
                                          href={`/guardians/${rent.guardian?.id}`}
                                          className="hidden-print-link"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/guardians/${rent.guardian?.id}`);
                                          }}
                                          style={{ color: '#000', textDecoration: 'none' }}
                                        >
                                          {rent.guardian?.user?.first_name} {rent.guardian?.user?.last_name}
                                        </a>
                                      </li>
                                      <li style={{
                                        fontSize: '80%',
                                        borderTop: '1px solid #e0e0e0',
                                        padding: '5px',
                                        color: '#000'
                                      }}>
                                        <span style={{
                                          width: '25%',
                                          display: 'inline-block',
                                          textAlign: 'right',
                                          fontWeight: 'bold',
                                          color: '#0a7280', // Laravel color
                                          marginRight: '5px'
                                        }}>Rate: </span>
                                        {formatCurrency(rent.monthly_payment || rent.license_fee || rent.payment || 0)}
                                      </li>
                                      <li style={{
                                        fontSize: '80%',
                                        borderTop: '1px solid #e0e0e0',
                                        padding: '5px',
                                        color: '#000'
                                      }}>
                                        <span style={{
                                          width: '25%',
                                          display: 'inline-block',
                                          textAlign: 'right',
                                          fontWeight: 'bold',
                                          color: '#0a7280', // Laravel color
                                          marginRight: '5px'
                                        }}>Start: </span>
                                        {rent.date_start ? formatDate(rent.date_start) : '-'}
                                      </li>
                                      <li style={{
                                        fontSize: '80%',
                                        borderTop: '1px solid #e0e0e0',
                                        padding: '5px',
                                        color: '#000'
                                      }}>
                                        <span style={{
                                          width: '25%',
                                          display: 'inline-block',
                                          textAlign: 'right',
                                          fontWeight: 'bold',
                                          color: '#0a7280', // Laravel color
                                          marginRight: '5px'
                                        }}>End: </span>
                                        {rent.date_end ? formatDate(rent.date_end) : ''}
                                      </li>
                                      {/* Notice date - only show if notice_to_quit exists (matching Laravel) */}
                                      {hasNoticeToQuit && noticeDate && (
                                        <li className="fs-13" style={{
                                          fontSize: '80%',
                                          borderTop: '1px solid #e0e0e0',
                                          padding: '5px',
                                          color: '#000'
                                        }}>
                                          <span style={{
                                            width: '25%',
                                            display: 'inline-block',
                                            textAlign: 'right',
                                            fontWeight: 'bold',
                                            color: '#0a7280', // Laravel color
                                            marginRight: '5px'
                                          }}>Not.: </span>
                                          {formatNoticeDate(noticeDate)}
                                        </li>
                                      )}
                                      <li style={{
                                        fontSize: '80%',
                                        borderTop: '1px solid #e0e0e0',
                                        padding: '5px',
                                        color: '#000'
                                      }}>
                                        <span style={{
                                          width: '25%',
                                          display: 'inline-block',
                                          textAlign: 'right',
                                          fontWeight: 'bold',
                                          color: '#0a7280', // Laravel color
                                          marginRight: '5px'
                                        }}>Keys: </span>
                                        {latestRent.room?.key_number || 0}
                                      </li>
                                      {/* Give notice button - only show if notice_to_quit doesn't exist (matching Laravel) */}
                                      {!hasNoticeToQuit && (
                                        <li className="smaller hidden-print" style={{
                                          fontSize: '85%',
                                          borderTop: '1px solid #e0e0e0',
                                          padding: '5px',
                                          color: '#000'
                                        }}>
                                          <a 
                                            href={`/rents/${latestRent.id}/notice-to-quit/create`}
                                            className="hidden-print-link"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              navigate(`/rents/${latestRent.id}/notice-to-quit/create`);
                                            }}
                                            style={{ color: '#000', textDecoration: 'none' }}
                                          >
                                            <span><i className="glyphicon glyphicon-menu-right"></i></span> Give notice
                                          </a>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Signed Agreement Column */}
                        <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                          {/* MATCH LARAVEL: Show signed agreement ONLY if signed_license_agreement (filename) exists */}
                          {latestRent.signed_license_agreement && (
                            <a
                              href="#"
                              onClick={(e) => { e.preventDefault(); handleViewLicenseAgreement(latestRent.id); }}
                              style={{ textDecoration: 'none' }}
                            >
                              <div style={{
                                display: 'flex',
                                minHeight: '60px',
                                backgroundColor: '#4c6bb3',
                                cursor: 'pointer',
                                color: '#fff',
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: '55px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#034ea2'
                                }}>
                                  <img src="/assets/images/pdf.png" alt="PDF" style={{ width: '20px', height: 'auto' }} />
                                </div>
                                <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                  <p style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px', margin: 0 }}>Signed Agreement</p>
                                  <p style={{ color: '#ddd', fontSize: '11px', margin: '3px 0 0 0' }}>Click to open & print</p>
                                </div>
                              </div>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="panel-group">
                      <div className="col-md-12">
                        You don't have active property! If you should have contact us, please.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Associated Documents Section */}
      {/* Laravel: vetted status (12) does NOT show associated documents section - uses search_for_property.blade.php */}
      {/* Laravel: payment_pending status (4) does NOT show associated documents section - uses payment_pending.blade.php */}
      {/* Laravel: awaiting_key_collection (5) with signed license does NOT show documents - uses final-message.blade.php */}
      {/* Hide for documents_required (2) - show upload area only, not the list of already-uploaded documents */}
      {(() => {
        const guardian = dashboardData?.guardian;
        const statusId = guardian?.status_id;
        const latestRentLocal = dashboardData?.latestRent;

        // Hide associated documents section for vetted status (12) - matching Laravel search_for_property.blade.php
        // Hide associated documents section for payment_pending status (4) - matching Laravel payment_pending.blade.php
        // Hide associated documents section for ALL awaiting_key_collection (5) - Laravel doesn't show documents for this status
        // Hide for documents_required (2) - guardian is still uploading; only Document Upload section should be shown
        if (statusId === 12 || statusId === 4 || statusId === 5 || statusId === 2) {
          return null;
        }
        
        const documents = dashboardData?.documents;
        const hasDocuments = documents && (
          (documents.passport && documents.passport.length > 0) ||
          (documents.bankStatements && documents.bankStatements.length > 0) ||
          (documents.employmentReference && documents.employmentReference.length > 0) ||
          (documents.payslips && documents.payslips.length > 0)
        );

        if (!hasDocuments) {
          return null;
        }

        const formatDate = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        };

        const handleDocumentClick = (document) => {
          if (document.url) {
            window.open(document.url, '_blank');
          } else if (document.download_url) {
            window.open(document.download_url, '_blank');
          }
        };

        // Collect all documents into a single array for grid display
        const allDocs = [];
        if (documents.passport) {
          documents.passport.forEach((doc, i) => allDocs.push({ ...doc, docType: 'Passport', key: `passport-${doc.id}` }));
        }
        if (documents.bankStatements) {
          documents.bankStatements.forEach((doc, i) => allDocs.push({ ...doc, docType: `Bank statement ${i + 1}`, key: `bank-${doc.id}` }));
        }
        if (documents.employmentReference) {
          documents.employmentReference.forEach((doc, i) => allDocs.push({ ...doc, docType: 'Employment reference', key: `emp-${doc.id}` }));
        }
        if (documents.payslips) {
          documents.payslips.forEach((doc, i) => allDocs.push({ ...doc, docType: `Payslip ${i + 1}`, key: `payslip-${doc.id}` }));
        }

        return (
          <section className={styles.mainContent}>
            <div className={styles.container}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'normal' }}>Associated documents</h3>
                <hr style={{ margin: '0 0 20px 0', borderColor: '#ddd' }} />
              </div>

              {/* 3-column grid for documents - MATCH LARAVEL */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '15px'
              }}>
                {allDocs.map((doc) => (
                  <a
                    key={doc.key}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleDocumentClick(doc); }}
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      display: 'flex',
                      minHeight: '55px',
                      backgroundColor: '#4c6bb3',
                      cursor: 'pointer',
                      color: '#fff',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#034ea2'
                      }}>
                        <img src="/assets/images/pdf.png" alt="PDF" style={{ width: '18px', height: 'auto' }} />
                      </div>
                      <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                        <p style={{ color: '#fff', fontWeight: 'bold', fontSize: '13px', margin: 0 }}>{doc.docType}</p>
                        <p style={{ color: '#ccc', fontSize: '11px', margin: '2px 0 0 0' }}>Uploaded {formatDate(doc.created_at)}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        );
      })()}


      {/* Signed License Agreement Section - Show for ALL guardians assigned to property with signed agreement */}
      {/* FIX: Guardians should be able to see their signed license agreement regardless of status */}
      {/* Issue: Portal issue #77 - Guardians complaining they can't see signed license agreement */}
      {/* Previous Guardian (7) does NOT show signed agreement - matching Laravel */}
      {/* Awaiting Key Collection (5) with signed license uses final-message.blade.php - doesn't show this section */}
      {(() => {
        const guardian = dashboardData?.guardian;
        const latestRent = dashboardData?.latestRent;
        const statusContent = getStatusSpecificContent(guardian, applicationStage, latestRent);
        const hasSignedAgreement = latestRent && latestRent.signed_license_agreement;
        const isPlacedOrNTQ = guardian?.status_id === 6 || guardian?.status_id === 10;
        const isAwaitingKeyCollectionSigned = guardian?.status_id === 5 && latestRent?.licence_date_signed;

        // Hide signed agreement for previous guardian (status 7) - matching Laravel
        if (statusContent.showSignedAgreement === false) {
          return null;
        }

        // Hide for awaiting_key_collection (5) with signed license - Laravel shows final-message.blade.php instead
        if (isAwaitingKeyCollectionSigned) {
          return null;
        }

        // Show signed agreement section if:
        // 1. Guardian has a rent (assigned to property)
        // 2. Agreement has been signed
        // 3. NOT already shown in "Current Property" section (i.e., not Placed/NTQ status)
        if (!hasSignedAgreement || isPlacedOrNTQ || !latestRent?.room?.property) {
          return null;
        }

        return (
          <section className={styles.mainContent}>
            <div className={styles.container}>
              <div className="row">
                <div className="col-md-12">
                  <div className="panel-title title-standard relative" style={{ marginBottom: '20px' }}>
                    <h3 className="mr-auto" style={{ margin: 0 }}>License Agreement</h3>
                  </div>
                  
                  <div className="panel-group">
                    <div className="row">
                      <div className="col-md-12">
                        {/* Show signed agreement for guardians assigned to property */}
                        <div className="property-application-license-agreement" style={{ marginTop: '30px' }}>
                          <a 
                            href="#"
                            className="hidden-print-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewLicenseAgreement(latestRent.id);
                            }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '15px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              backgroundColor: '#f8f9fa',
                              maxWidth: '400px'
                            }}>
                              <div style={{ marginRight: '15px' }}>
                                <img src="/assets/images/pdf.png" alt="PDF" style={{ width: '40px', height: '40px' }} />
                              </div>
                              <div>
                                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>Signed Agreement</p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Click to open & print</p>
                                {latestRent.licence_date_signed && (
                                  <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#999' }}>
                                    Signed: {new Date(latestRent.licence_date_signed).toLocaleDateString('en-GB')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Available Guardian Properties Section - For Previous Guardian (status 7) - Matching Laravel */}
      {(() => {
        const guardian = dashboardData?.guardian;
        const statusId = guardian?.status_id;
        const statusContent = getStatusSpecificContent(guardian, applicationStage, dashboardData?.latestRent);
        
        // Show "Available guardian properties" section for previous guardian (status 7) - matching Laravel
        if (statusId === 7 && statusContent.showAvailableSpaces) {
          return (
            <section className={styles.mainContent}>
              <div className={styles.container}>
                <div className={styles.availableProperties}>
                  <h3 className={styles.availablePropertiesTitle}>
                    Available guardian properties
                  </h3>
                  <p className={styles.availablePropertiesText}>
                    Explore our available guardian properties using the buttons below. "Search Properties" to find available spaces, "My Viewings" to see your scheduled property viewings, and "Keep Me Informed" to stay updated on properties with upcoming viewings.
                  </p>
                  <div className={styles.actionButtons}>
                    <a
                      href="/spaces/available"
                      className={styles.actionButton}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/spaces/available');
                      }}
                    >
                      Search properties
                    </a>
                    <a
                      href="/guardian-viewings"
                      className={styles.actionButton}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/guardian-viewings');
                      }}
                    >
                      My Viewings
                    </a>
                    <a
                      href="/keep-me-informed"
                      className={styles.actionButton}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/keep-me-informed');
                      }}
                    >
                      Keep me informed
                    </a>
                  </div>
                </div>
              </div>
            </section>
          );
        }
        return null;
      })()}

      {/* Main Content Section */}
      {/* Hide intro text and application process for previous guardian (status 7) - matching Laravel */}
      {/* Hide intro/stepper for status 5 with signed agreement - Laravel uses final-message.blade.php */}
      {(() => {
        const guardian = dashboardData?.guardian;
        const statusId = guardian?.status_id;
        const latestRentLocal = dashboardData?.latestRent;

        // Hide main content section for previous guardian (status 7) - matching Laravel structure
        if (statusId === 7) {
          return null;
        }

        // Check if this is awaiting key collection (status 5) - both signed and unsigned
        const isAwaitingKeyCollection = statusId === 5;
        const isAwaitingKeyCollectionSigned = statusId === 5 && latestRentLocal?.licence_date_signed;

        // Placed (6) and NTQ (10) guardians: Laravel renders placed.blade.php which omits
        // the "Property application process" stage headers entirely.
        const isPlacedOrNTQ = statusId === 6 || statusId === 10;

        return (
          <section className={styles.mainContent}>
            <div className={styles.container}>
              {/* Hide intro text for status 5, placed (6)/NTQ (10), OR status 4 with payment_confirmed - Laravel doesn't show this for these statuses */}
              {!isAwaitingKeyCollection && !isPlacedOrNTQ && !(statusId === 4 && (latestRentLocal?.payment_confirmed == 1 || latestRentLocal?.payment_confirmed === true)) && (
                <div className={styles.introText}>
                  This section guides you through the steps required to search for
                  properties, request viewings, and stay informed about available
                  properties, ensuring you find the perfect home as a guardian.
                </div>
              )}

              {/* Hide entire applicationProcess section for status 5 - Laravel doesn't show this for awaiting key collection */}
              {!isAwaitingKeyCollection && (
              <div className={styles.applicationProcess}>
                    {/* Hide the "Property application process" title + stage stepper for placed (6) and NTQ (10).
                        Laravel renders placed.blade.php which omits the stage headers, but the available
                        spaces / stage content below is still shown. */}
                    {!isPlacedOrNTQ && (
                      <>
                        <h3 className={styles.processTitle}>
                          Property application process: {latestRent?.room?.property?.name && latestRent?.room?.property?.address_1 && latestRent?.room?.property?.town && latestRent?.room?.property?.postcode
                            ? `${latestRent.room.property.address_1}${latestRent.room.property.address_2 ? ', ' + latestRent.room.property.address_2 : ''}${latestRent.room.property.address_3 ? ', ' + latestRent.room.property.address_3 : ''}, ${latestRent.room.property.town}, ${latestRent.room.property.postcode}`
                            : ''}
                        </h3>

                        {/* Use GuardianStageHeader component with backend data */}
                        {/* Laravel: payment_pending uses active: 'stage_4' which means Stage 3: Payment Pending is active */}
                        <GuardianStageHeader
                          active={
                            applicationStage?.active === 'stage_2' || applicationStage?.current === 'stage_1'
                              ? 1
                              : applicationStage?.active === 'stage_3' || applicationStage?.current === 'stage_2'
                              ? 2
                              : applicationStage?.active === 'stage_4' || applicationStage?.current === 'stage_3'
                              ? 3
                              : 1 // Default to Stage 1
                          }
                          sent={applicationStage?.sent}
                          paymentConfirmed={applicationStage?.paymentConfirmed}
                        />
                      </>
                    )}

            {/* Render content based on application stage (matching Laravel logic) */}
            {(() => {
              if (applicationStage?.current === 'stage_1') {
                return (
                  <div className={styles.availableProperties}>
                    <h3 className={styles.availablePropertiesTitle}>
                      Property application stage 1
                    </h3>
                    <div className={styles.availablePropertiesText}>
                      {applicationStage.sent
                        ? 'Thank you for providing documentation. We are reviewing it and will get back to you once completed.'
                        : 'Thank you for registering with Global Guardians! To become a guardian with us, please complete your application by providing the required documents. These documents are stored securely and verified by our auditors.'}
                    </div>
                    {!applicationStage.sent && (
                      <div className={styles.stageBox}>
                        {/* Employment History Section */}
                        <div className={styles.employmentHistorySection}>
                          <div className={styles.sectionHeader}>
                            <h4 className={styles.sectionTitle}>
                              Employment History
                            </h4>
                            <button
                              type="button"
                              className={styles.btnAddEmployment}
                              onClick={() => setShowEmploymentForm(true)}
                            >
                              Add Employment History
                            </button>
                          </div>

                          <div className={styles.employmentNote}>
                            Global Guardians requires you to complete your
                            employment history to cover the past 2 years, in
                            accordance with British Standard BS8584 screening
                            requirements. All guardians provide this information
                            to make sure the people living in our properties are
                            responsible, working people. Please click on the
                            ‘Add employment’ button to begin.
                          </div>

                          {/* Employment History Table */}
                          <div className={styles.employmentTableContainer}>
                            <div className={styles.tableControls}>
                              <div className={styles.showEntries}>
                                <label>Show:</label>
                                <select defaultValue="10">
                                  <option value="10">10</option>
                                  <option value="25">25</option>
                                  <option value="50">50</option>
                                </select>
                                <span>entries</span>
                              </div>
                              <div className={styles.searchBox}>
                                <label>Search:</label>
                                <input
                                  type="text"
                                  placeholder="Search employment history..."
                                />
                              </div>
                            </div>

                            <div className={styles.employmentTable}>
                              <table>
                                <thead>
                                  <tr>
                                    <th 
                                      className="sortable"
                                      onClick={() => handleEmploymentSort("company_name")}
                                      style={{ cursor: "pointer" }}
                                    >
                                      Company name<SortIcon direction={employmentSortField === "company_name" ? employmentSortDirection : null} isActive={employmentSortField === "company_name"} />
                                    </th>
                                    <th 
                                      className="sortable"
                                      onClick={() => handleEmploymentSort("date_start")}
                                      style={{ cursor: "pointer" }}
                                    >
                                      Start Date<SortIcon direction={employmentSortField === "date_start" ? employmentSortDirection : null} isActive={employmentSortField === "date_start"} />
                                    </th>
                                    <th 
                                      className="sortable"
                                      onClick={() => handleEmploymentSort("date_end")}
                                      style={{ cursor: "pointer" }}
                                    >
                                      End Date<SortIcon direction={employmentSortField === "date_end" ? employmentSortDirection : null} isActive={employmentSortField === "date_end"} />
                                    </th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loadingEmployment ? (
                                    <tr>
                                      <td
                                        colSpan="4"
                                        className={styles.loadingCell}
                                      >
                                        Loading employment history...
                                      </td>
                                    </tr>
                                  ) : sortedEmploymentHistory.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan="4"
                                        className={styles.emptyCell}
                                      >
                                        No employment history found. Click "Add
                                        Employment History" to get started.
                                      </td>
                                    </tr>
                                  ) : (
                                    sortedEmploymentHistory.map((record) => (
                                      <tr
                                        key={record.id}
                                        className={
                                          deletingEmploymentId === record.id &&
                                          isDeleting
                                            ? styles.deletingRow
                                            : ''
                                        }
                                      >
                                        <td>
                                          {deletingEmploymentId === record.id &&
                                          isDeleting ? (
                                            <div
                                              className={
                                                styles.deletingIndicator
                                              }
                                            >
                                              <span
                                                className={styles.spinner}
                                              ></span>
                                              Deleting...
                                            </div>
                                          ) : (
                                            record.company_name
                                          )}
                                        </td>
                                        <td>
                                          {record.date_start
                                            ? new Date(
                                                record.date_start
                                              ).toLocaleDateString('en-GB')
                                            : 'N/A'}
                                        </td>
                                        <td>
                                          {record.still_in_employment
                                            ? 'Still working there'
                                            : record.date_end
                                            ? new Date(
                                                record.date_end
                                              ).toLocaleDateString('en-GB')
                                            : 'N/A'}
                                        </td>
                                        <td className={styles.actionButtons}>
                                          <button
                                            type="button"
                                            className={`${styles.btnAction} ${styles.btnShow}`}
                                            onClick={() =>
                                              handleShowEmployment(record)
                                            }
                                            disabled={
                                              deletingEmploymentId ===
                                                record.id && isDeleting
                                            }
                                          >
                                            Show
                                          </button>
                                          <button
                                            type="button"
                                            className={`${styles.btnAction} ${styles.btnEdit}`}
                                            onClick={() =>
                                              handleEditEmployment(record)
                                            }
                                            disabled={
                                              deletingEmploymentId ===
                                                record.id && isDeleting
                                            }
                                          >
                                            Edit
                                          </button>
                                          <button
                                            type="button"
                                            className={`${styles.btnAction} ${styles.btnRemove}`}
                                            onClick={() =>
                                              handleDeleteEmployment(record.id)
                                            }
                                            disabled={
                                              deletingEmploymentId ===
                                                record.id && isDeleting
                                            }
                                          >
                                            Remove
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>

                            <div className={styles.tableFooter}>
                              <div className={styles.entriesInfo}>
                                Showing {employmentHistory.length > 0 ? 1 : 0}{' '}
                                to {employmentHistory.length} of{' '}
                                {employmentHistory.length} entries
                              </div>
                              <div className={styles.pagination}>
                                <button disabled>Previous</button>
                                <span className={styles.currentPage}>1</span>
                                <button disabled>Next</button>
                              </div>
                            </div>
                          </div>

                          {/* Employment Form Modal */}
                          {showEmploymentForm && (
                            <div className={styles.employmentFormModal}>
                              <div className={styles.modalContent}>
                                <div className={styles.modalHeader}>
                                  <h4>
                                    {editingEmploymentId
                                      ? 'Edit Employment History'
                                      : 'Add Employment History'}
                                  </h4>
                                  <button
                                    type="button"
                                    className={styles.closeButton}
                                    onClick={resetEmploymentForm}
                                  >
                                    ×
                                  </button>
                                </div>

                                <form
                                  onSubmit={handleEmploymentSubmit}
                                  className={styles.employmentForm}
                                >
                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Start Date *</label>
                                      <input
                                        type="date"
                                        name="date_start"
                                        value={employmentForm.date_start || ''}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'date_start',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.date_start ? styles.inputError : ''}
                                        required
                                      />
                                      {employmentFormErrors.date_start && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.date_start}
                                        </small>
                                      )}
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>
                                        Date End{employmentForm.still_in_employment === '0' ? ' *' : ''}
                                      </label>
                                      <input
                                        type="date"
                                        name="date_end"
                                        value={employmentForm.date_end}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'date_end',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.date_end ? styles.inputError : ''}
                                        disabled={
                                          employmentForm.still_in_employment ===
                                          '1'
                                        }
                                        required={employmentForm.still_in_employment === '0'}
                                        placeholder="DD/MM/YYYY"
                                      />
                                      {employmentFormErrors.date_end && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.date_end}
                                        </small>
                                      )}
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Still In Employment</label>
                                      <select
                                        name="still_in_employment"
                                        value={
                                          employmentForm.still_in_employment
                                        }
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'still_in_employment',
                                            e.target.value
                                          )
                                        }
                                      >
                                        <option value="0">No</option>
                                        <option value="1">Yes</option>
                                      </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>Company Name *</label>
                                      <input
                                        type="text"
                                        name="company_name"
                                        value={employmentForm.company_name}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'company_name',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.company_name ? styles.inputError : ''}
                                        placeholder="Placeholder"
                                        required
                                      />
                                      {employmentFormErrors.company_name && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.company_name}
                                        </small>
                                      )}
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Address 1 *</label>
                                      <input
                                        type="text"
                                        name="address_1"
                                        value={employmentForm.address_1}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'address_1',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.address_1 ? styles.inputError : ''}
                                        required
                                        placeholder="Placeholder"
                                      />
                                      {employmentFormErrors.address_1 && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.address_1}
                                        </small>
                                      )}
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>Address 2</label>
                                      <input
                                        type="text"
                                        name="address_2"
                                        value={employmentForm.address_2}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'address_2',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Placeholder"
                                      />
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Address 3</label>
                                      <input
                                        type="text"
                                        name="address_3"
                                        value={employmentForm.address_3}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'address_3',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Placeholder"
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>Town *</label>
                                      <input
                                        type="text"
                                        name="town"
                                        value={employmentForm.town}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'town',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.town ? styles.inputError : ''}
                                        placeholder="Placeholder"
                                        required
                                      />
                                      {employmentFormErrors.town && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.town}
                                        </small>
                                      )}
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Postcode *</label>
                                      <input
                                        type="text"
                                        name="postcode"
                                        value={employmentForm.postcode}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'postcode',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.postcode ? styles.inputError : ''}
                                        placeholder="Placeholder"
                                        required
                                      />
                                      {employmentFormErrors.postcode && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.postcode}
                                        </small>
                                      )}
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>Referee Name</label>
                                      <input
                                        type="text"
                                        name="referee_name"
                                        value={employmentForm.referee_name}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'referee_name',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Placeholder"
                                      />
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Referee Telephone</label>
                                      <input
                                        type="tel"
                                        name="referee_telephone"
                                        value={employmentForm.referee_telephone}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'referee_telephone',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Placeholder"
                                      />
                                    </div>
                                    <div className={styles.formGroup}>
                                      <label>Referee Email</label>
                                      <input
                                        type="email"
                                        name="referee_email"
                                        value={employmentForm.referee_email}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'referee_email',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Placeholder"
                                      />
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Job Title *</label>
                                      <input
                                        type="text"
                                        name="job_title"
                                        value={employmentForm.job_title}
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'job_title',
                                            e.target.value
                                          )
                                        }
                                        className={employmentFormErrors.job_title ? styles.inputError : ''}
                                        placeholder="Placeholder"
                                        required
                                      />
                                      {employmentFormErrors.job_title && (
                                        <small className={styles.errorText}>
                                          {employmentFormErrors.job_title}
                                        </small>
                                      )}
                                    </div>
                                  </div>

                                  <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                      <label>Reason for leaving</label>
                                      <textarea
                                        value={
                                          employmentForm.reason_for_leaving
                                        }
                                        onChange={(e) =>
                                          handleEmploymentFormChange(
                                            'reason_for_leaving',
                                            e.target.value
                                          )
                                        }
                                        rows="3"
                                        placeholder="Placeholder"
                                      />
                                    </div>
                                  </div>

                                  <div className={styles.formActions}>
                                    <button
                                      type="button"
                                      className={styles.btnCancel}
                                      onClick={resetEmploymentForm}
                                    >
                                      Close
                                    </button>
                                    <button
                                      type="submit"
                                      className={styles.btnSubmit}
                                      disabled={submittingEmployment}
                                    >
                                      {submittingEmployment
                                        ? 'Saving...'
                                        : editingEmploymentId
                                        ? 'Update Employment History'
                                        : 'Add Employment History'}
                                    </button>
                                  </div>
                                </form>
                              </div>
                            </div>
                          )}

                          {/* View Employment Modal */}
                          {showViewModal && selectedEmployment && (
                            <div className={styles.employmentFormModal}>
                              <div className={styles.modalContent}>
                                <div className={styles.modalHeader}>
                                  <h4>Employment History</h4>
                                  <button
                                    type="button"
                                    className={styles.closeButton}
                                    onClick={() => {
                                      setShowViewModal(false);
                                      setSelectedEmployment(null);
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>

                                <div className={styles.employmentViewContent}>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Date start:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.date_start
                                        ? new Date(
                                            selectedEmployment.date_start
                                          ).toLocaleDateString('en-GB')
                                        : 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Date end:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.still_in_employment
                                        ? 'Still working there'
                                        : selectedEmployment.date_end
                                        ? new Date(
                                            selectedEmployment.date_end
                                          ).toLocaleDateString('en-GB')
                                        : 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Company name:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.company_name || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Address 1:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.address_1 || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Address 2:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.address_2 || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Address 3:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.address_3 || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Town:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.town || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Postcode:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.postcode || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Referee name:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.referee_name || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Referee telephone:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.referee_telephone ||
                                        'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Referee email:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.referee_email ||
                                        'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Job title:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.job_title || 'N/A'}
                                    </div>
                                  </div>
                                  <div className={styles.viewRow}>
                                    <div className={styles.viewLabel}>
                                      Reason for leaving:
                                    </div>
                                    <div className={styles.viewValue}>
                                      {selectedEmployment.reason_for_leaving ||
                                        'N/A'}
                                    </div>
                                  </div>
                                </div>

                                <div className={styles.modalFooter}>
                                  <button
                                    type="button"
                                    className={styles.btnCancel}
                                    onClick={() => {
                                      setShowViewModal(false);
                                      setSelectedEmployment(null);
                                    }}
                                  >
                                    Close
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Delete Confirmation Modal */}
                          {showDeleteModal && (
                            <div className={styles.employmentFormModal}>
                              <div className={styles.modalContent}>
                                <div className={styles.modalHeader}>
                                  <h4>Confirm Delete</h4>
                                  <button
                                    type="button"
                                    className={styles.closeButton}
                                    onClick={handleCloseDeleteModal}
                                    disabled={isDeleting}
                                  >
                                    ×
                                  </button>
                                </div>

                                <div
                                  className={styles.deleteConfirmationContent}
                                >
                                  <p>
                                    Are you sure you want to delete this
                                    employment record?
                                  </p>
                                  <p>
                                    <strong>
                                      {employmentHistory.find(
                                        (r) => r.id === deletingEmploymentId
                                      )?.company_name || 'Unknown Company'}
                                    </strong>
                                  </p>
                                  <p>This action cannot be undone.</p>
                                </div>

                                <div className={styles.modalFooter}>
                                  <button
                                    type="button"
                                    className={styles.btnCancel}
                                    onClick={cancelDeleteEmployment}
                                    disabled={isDeleting}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.btnSubmit} ${styles.btnDanger}`}
                                    onClick={confirmDeleteEmployment}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? (
                                      <>
                                        <span className={styles.spinner}></span>
                                        Deleting...
                                      </>
                                    ) : (
                                      'Delete'
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Document Upload Section */}
                        <div className={styles.documentUploadSection}>
                          <h4 className={styles.sectionTitle}>
                            Document Upload
                          </h4>

                          {/* Passport */}
                          <div className={styles.uploadItem}>
                            <label className={styles.uploadLabel}>
                              Passport{' '}
                              <span className={styles.required}>*</span>
                            </label>
                            <S3DocumentUpload
                              folder={`guardian-documents/passport/${
                                dashboardData?.guardian?.id || 'me'
                              }`}
                              onUploadSuccess={handleStage1Upload('passport')}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              maxSize={20 * 1024 * 1024}
                              helpText="Drag & Drop your files or Browse"
                              uploadedFile={stage1Docs['passport']}
                              onRemoveFile={() =>
                                handleRemoveStage1File('passport')
                              }
                            />
                            <div className={styles.uploadInstruction}>
                              If you have a UK passport please upload this
                              document. If you do not hold a UK passport please
                              upload your right to work document, one of the
                              following: Biometric Residence Permit, Residence
                              Cards (Biometric Format), Citizens of the United
                              Kingdom and Common Travel Area (including the
                              right of abode), Nationals from the European
                              Economic Area (EEA) and their non EEA family
                              members or Non EEA migrants with a right to work.
                            </div>
                            {stage1Docs['passport'] && (
                              <div className={styles.uploadedHint}>
                                Uploaded
                              </div>
                            )}
                          </div>

                          {/* 3 Bank Statements */}
                          <div className={styles.uploadItem}>
                            <label className={styles.uploadLabel}>
                              3 bank statements{' '}
                              <span className={styles.required}>*</span>
                            </label>
                            <div className={styles.bankStatementsGrid}>
                              <S3DocumentUpload
                                folder={`guardian-documents/bank-statements/${
                                  dashboardData?.guardian?.id || 'me'
                                }`}
                                onUploadSuccess={handleStage1Upload(
                                  'bank_statement_1'
                                )}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxSize={20 * 1024 * 1024}
                                helpText="Drag & Drop your files or Browse"
                                uploadedFile={stage1Docs['bank_statement_1']}
                                onRemoveFile={() =>
                                  handleRemoveStage1File('bank_statement_1')
                                }
                              />
                              <S3DocumentUpload
                                folder={`guardian-documents/bank-statements/${
                                  dashboardData?.guardian?.id || 'me'
                                }`}
                                onUploadSuccess={handleStage1Upload(
                                  'bank_statement_2'
                                )}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxSize={20 * 1024 * 1024}
                                helpText="Drag & Drop your files or Browse"
                                uploadedFile={stage1Docs['bank_statement_2']}
                                onRemoveFile={() =>
                                  handleRemoveStage1File('bank_statement_2')
                                }
                              />
                              <S3DocumentUpload
                                folder={`guardian-documents/bank-statements/${
                                  dashboardData?.guardian?.id || 'me'
                                }`}
                                onUploadSuccess={handleStage1Upload(
                                  'bank_statement_3'
                                )}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxSize={20 * 1024 * 1024}
                                helpText="Drag & Drop your files or Browse"
                                uploadedFile={stage1Docs['bank_statement_3']}
                                onRemoveFile={() =>
                                  handleRemoveStage1File('bank_statement_3')
                                }
                              />
                            </div>
                            {stage1Docs['bank_statement_1'] &&
                              stage1Docs['bank_statement_2'] &&
                              stage1Docs['bank_statement_3'] && (
                                <div className={styles.uploadedHint}>
                                  All bank statements uploaded
                                </div>
                              )}
                          </div>

                          {/* Employment Reference */}
                          <div className={styles.uploadItem}>
                            <label className={styles.uploadLabel}>
                              Employment reference{' '}
                              <span className={styles.required}>*</span>
                            </label>
                            <S3DocumentUpload
                              folder={`guardian-documents/employment-reference/${
                                dashboardData?.guardian?.id || 'me'
                              }`}
                              onUploadSuccess={handleStage1Upload(
                                'employment_reference'
                              )}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              maxSize={20 * 1024 * 1024}
                              helpText="Drag & Drop your files or Browse"
                              uploadedFile={stage1Docs['employment_reference']}
                              onRemoveFile={() =>
                                handleRemoveStage1File('employment_reference')
                              }
                            />
                            {stage1Docs['employment_reference'] && (
                              <div className={styles.uploadedHint}>
                                Uploaded
                              </div>
                            )}
                          </div>

                          {/* 3 Recent Payslips */}
                          <div className={styles.uploadItem}>
                            <label className={styles.uploadLabel}>
                              3 recent payslips{' '}
                              <span className={styles.required}>*</span>
                            </label>
                            <div className={styles.payslipsGrid}>
                              <S3DocumentUpload
                                folder={`guardian-documents/payslips/${
                                  dashboardData?.guardian?.id || 'me'
                                }`}
                                onUploadSuccess={handleStage1Upload(
                                  'payslip_1'
                                )}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxSize={20 * 1024 * 1024}
                                helpText="Drag & Drop your files or Browse"
                                uploadedFile={stage1Docs['payslip_1']}
                                onRemoveFile={() =>
                                  handleRemoveStage1File('payslip_1')
                                }
                              />
                              <S3DocumentUpload
                                folder={`guardian-documents/payslips/${
                                  dashboardData?.guardian?.id || 'me'
                                }`}
                                onUploadSuccess={handleStage1Upload(
                                  'payslip_2'
                                )}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxSize={20 * 1024 * 1024}
                                helpText="Drag & Drop your files or Browse"
                                uploadedFile={stage1Docs['payslip_2']}
                                onRemoveFile={() =>
                                  handleRemoveStage1File('payslip_2')
                                }
                              />
                              <S3DocumentUpload
                                folder={`guardian-documents/payslips/${
                                  dashboardData?.guardian?.id || 'me'
                                }`}
                                onUploadSuccess={handleStage1Upload(
                                  'payslip_3'
                                )}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                maxSize={20 * 1024 * 1024}
                                helpText="Drag & Drop your files or Browse"
                                uploadedFile={stage1Docs['payslip_3']}
                                onRemoveFile={() =>
                                  handleRemoveStage1File('payslip_3')
                                }
                              />
                            </div>
                            {stage1Docs['payslip_1'] &&
                              stage1Docs['payslip_2'] &&
                              stage1Docs['payslip_3'] && (
                                <div className={styles.uploadedHint}>
                                  All payslips uploaded
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Personal details section */}
                        <div className={styles.stage1Details}>
                          <div className={styles.detailsHeader}>
                            Personal details:
                          </div>
                          <div className={styles.detailsNote}>
                            Please complete all the fields below so that we can
                            process your application to become a Global
                            Guardian.
                          </div>
                          <div className={styles.detailsGrid}>
                            <div className={styles.detailItemWide}>
                              <div className={styles.detailLabel}>
                                Profile Picture{' '}
                                <span className={styles.required}>*</span>
                              </div>
                              <div className={styles.profileUploadRow}>
                                <div className={styles.profilePreview}>
                                  {profilePreviewUrl ? (
                                    <img
                                      alt="Profile Preview"
                                      src={profilePreviewUrl}
                                      className={styles.profileImg}
                                      onError={(e) => {
                                        console.error('[PROFILE] Image failed to load:', profilePreviewUrl);
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  ) : profilePicKey && profilePicKey.key ? (
                                    <div className={styles.profilePlaceholder}>
                                      Loading preview...
                                    </div>
                                  ) : (
                                    <div className={styles.profilePlaceholder}>
                                      No image
                                    </div>
                                  )}
                                </div>
                                <div className={styles.profileActions}>
                                  <S3DocumentUpload
                                    folder="uploads/images/users"
                                    onUploadSuccess={handleProfileUpload}
                                    accept=".jpg,.jpeg,.png,.webp"
                                    maxSize={10 * 1024 * 1024}
                                    helpText="Drag & Drop your files or Browse"
                                    showPreview={true}
                                    onPreviewChange={setProfilePreviewUrl}
                                    uploadedFile={profilePicKey}
                                    onRemoveFile={handleRemoveProfilePic}
                                  />
                                  {profilePicKey && (
                                    <button
                                      type="button"
                                      className={styles.btnSmall}
                                      onClick={handleRemoveProfilePic}
                                    >
                                      Delete Image
                                    </button>
                                  )}
                                </div>
                              </div>
                              {hasAttemptedSubmit && validationErrors.profilePicture && (
                                <small className={styles.errorText}>
                                  {validationErrors.profilePicture}
                                </small>
                              )}
                            </div>

                            <div className={styles.detailItem}>
                              <label className={styles.detailLabel}>
                                How did you hear about Global Guardians?
                              </label>
                              <select
                                className={styles.formInput}
                                value={heardAbout}
                                onChange={(e) => {
                                  setHeardAbout(e.target.value);
                                  setHasUserInteracted(true); // ✅ Mark user interaction
                                }}
                              >
                                <option value="">Please Select</option>
                                {heardAboutOptions.map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {option.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className={styles.detailItem}>
                              <label className={styles.detailLabel}>
                                Date Of Birth{' '}
                                <span className={styles.required}>*</span>
                              </label>
                              <input
                                type="date"
                                className={`${styles.formInput} ${
                                  hasAttemptedSubmit && validationErrors.dateOfBirth
                                    ? styles.inputError
                                    : ''
                                }`}
                                value={dateOfBirth}
                                onChange={(e) => {
                                  setDateOfBirth(e.target.value);
                                  setHasUserInteracted(true); // ✅ Mark user interaction
                                }}
                                max={new Date().toISOString().split('T')[0]} // ✅ Prevent future dates in date picker
                                min="1920-01-01" // ✅ Reasonable minimum date
                              />
                              {hasAttemptedSubmit && validationErrors.dateOfBirth && (
                                <small className={styles.errorText}>
                                  {validationErrors.dateOfBirth}
                                </small>
                              )}
                            </div>

                            <div className={styles.detailItem}>
                              <label className={styles.detailLabel}>
                                Any Criminal Record?{' '}
                                <span className={styles.required}>*</span>
                              </label>
                              <div
                                className={`${styles.inlineRadios} ${
                                  hasAttemptedSubmit && validationErrors.criminalRecord
                                    ? styles.hasError
                                    : ''
                                }`}
                              >
                                <label>
                                  <input
                                    type="radio"
                                    name="criminalRecord"
                                    checked={criminalRecord === false}
                                    onChange={() => {
                                      setCriminalRecord(false);
                                      setCriminalRecordDescription(''); // Clear description when No is selected
                                      setHasUserInteracted(true); // ✅ Mark user interaction
                                    }}
                                  />{' '}
                                  No
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name="criminalRecord"
                                    checked={criminalRecord === true}
                                    onChange={() => {
                                      setCriminalRecord(true);
                                      setHasUserInteracted(true); // ✅ Mark user interaction
                                    }}
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              {hasAttemptedSubmit && validationErrors.criminalRecord && (
                                <small className={styles.errorText}>
                                  {validationErrors.criminalRecord}
                                </small>
                              )}
                            </div>

                            {/* ✅ NEW: Conditional Criminal Record Description (Laravel-compatible) */}
                            {criminalRecord === true && (
                              <div className={styles.detailItemWide}>
                                <label className={styles.detailLabel}>
                                  Criminal Record Description{' '}
                                  <span className={styles.required}>*</span>
                                </label>
                                <textarea
                                  className={`${styles.formInput} ${
                                    hasAttemptedSubmit && validationErrors.criminalRecordDescription
                                      ? styles.inputError
                                      : ''
                                  }`}
                                  value={criminalRecordDescription}
                                  onChange={(e) => {
                                    setCriminalRecordDescription(e.target.value);
                                    setHasUserInteracted(true); // ✅ Mark user interaction
                                  }}
                                  placeholder="Please provide details of your criminal record..."
                                  rows={4}
                                />
                                {hasAttemptedSubmit && validationErrors.criminalRecordDescription && (
                                  <small className={styles.errorText}>
                                    {validationErrors.criminalRecordDescription}
                                  </small>
                                )}
                              </div>
                            )}

                            <div className={styles.detailItem}>
                              <label className={styles.detailLabel}>
                                Any Medical Condition?{' '}
                                <span>
                                  <span className={styles.required}>*</span>
                                </span>
                              </label>
                              <div
                                className={`${styles.inlineRadios} ${
                                  hasAttemptedSubmit && validationErrors.medicalCondition
                                    ? styles.hasError
                                    : ''
                                }`}
                              >
                                <label>
                                  <input
                                    type="radio"
                                    name="medicalCondition"
                                    checked={medicalCondition === false}
                                    onChange={() => {
                                      setMedicalCondition(false);
                                      setMedicalConditionDescription(''); // Clear description when No is selected
                                      setHasUserInteracted(true); // ✅ Mark user interaction
                                    }}
                                  />{' '}
                                  No
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name="medicalCondition"
                                    checked={medicalCondition === true}
                                    onChange={() => {
                                      setMedicalCondition(true);
                                      setHasUserInteracted(true); // ✅ Mark user interaction
                                    }}
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              {hasAttemptedSubmit && validationErrors.medicalCondition && (
                                <small className={styles.errorText}>
                                  {validationErrors.medicalCondition}
                                </small>
                              )}
                            </div>

                            {/* ✅ NEW: Conditional Medical Condition Description (Laravel-compatible) */}
                            {medicalCondition === true && (
                              <div className={styles.detailItemWide}>
                                <label className={styles.detailLabel}>
                                  Medical Condition Description{' '}
                                  <span className={styles.required}>*</span>
                                </label>
                                <textarea
                                  className={`${styles.formInput} ${
                                    hasAttemptedSubmit && validationErrors.medicalConditionDescription
                                      ? styles.inputError
                                      : ''
                                  }`}
                                  value={medicalConditionDescription}
                                  onChange={(e) => {
                                    setMedicalConditionDescription(e.target.value);
                                    setHasUserInteracted(true); // ✅ Mark user interaction
                                  }}
                                  placeholder="Please provide details of your medical condition..."
                                  rows={4}
                                />
                                {hasAttemptedSubmit && validationErrors.medicalConditionDescription && (
                                  <small className={styles.errorText}>
                                    {validationErrors.medicalConditionDescription}
                                  </small>
                                )}
                              </div>
                            )}

                            <div className={styles.detailItemWide}>
                              <label className={styles.detailLabel}>
                                Please provide the details of your next of kin
                                in case of emergency{' '}
                                <span className={styles.required}>*</span>
                              </label>
                              <textarea
                                className={`${styles.formInput} ${
                                  hasAttemptedSubmit && validationErrors.nextOfKin
                                    ? styles.inputError
                                    : ''
                                }`}
                                value={nextOfKin}
                                onChange={(e) => {
                                  setNextOfKin(e.target.value);
                                  setHasUserInteracted(true); // ✅ Mark user interaction
                                }}
                                placeholder="Name, relationship and contact number"
                                rows={4}
                              />
                              {hasAttemptedSubmit && validationErrors.nextOfKin && (
                                <small className={styles.errorText}>
                                  {validationErrors.nextOfKin}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className={styles.withCenterButton}>
                          <button
                            type="button"
                            onClick={handleSubmitStage1}
                            className={styles.btnMyglobeSubmit}
                            disabled={submittingStage1}
                          >
                            {submittingStage1
                              ? 'Submitting…'
                              : 'Submit documentation'}
                          </button>
                          {hasAttemptedSubmit && 
                            Object.keys(validationErrors).length > 0 && (
                              <div className={styles.validationSummary}>
                                <small
                                  style={{
                                    color: '#dc3545',
                                    marginTop: '8px',
                                    display: 'block',
                                  }}
                                >
                                  Please fix the following issues:{' '}
                                  {Object.values(validationErrors)
                                    .slice(0, 3)
                                    .join(', ')}
                                  {Object.keys(validationErrors).length > 3 &&
                                    '...'}
                                </small>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // ✅ IMPLEMENTATION 3: Dynamic Available Spaces Section using Status Content
              const statusContent = getStatusSpecificContent(dashboardData?.guardian, applicationStage, dashboardData?.latestRent);
              
              // Only render available spaces section if guardian can see available spaces
              if (statusContent.showAvailableSpaces) {
                return (
                  <div className={styles.availableProperties}>
                    <h3 className={styles.availablePropertiesTitle}>
                      Available guardian properties
                    </h3>
                    <p className={styles.availablePropertiesText}>
                      Explore our available guardian properties using the buttons below. "Search Properties" to find available spaces, "My Viewings" to see your scheduled property viewings, and "Keep Me Informed" to stay updated on properties with upcoming viewings.
                    </p>
                    <div className={styles.actionButtons}>
                      <a
                        href="/spaces/available"
                        className={styles.actionButton}
                      >
                        Search properties
                      </a>
                      <a
                        href="/guardian-viewings"
                        className={styles.actionButton}
                      >
                        My Viewings
                      </a>
                      <a
                        href="/keep-me-informed"
                        className={styles.actionButton}
                      >
                        Keep me informed
                      </a>
                    </div>
                  </div>
                );
              }

              // For statuses that don't show available spaces, show stage-specific content
              if (applicationStage?.current === 'stage_3') {
                const isAwaitingKeyCollection = statusContent.paymentConfirmed;
                const requiresSignature = statusContent.requiresSignature;
                const isFinalMessage = statusContent.isFinalMessage;
                const isPendingPayment = !isAwaitingKeyCollection && !requiresSignature && !isFinalMessage;

                // Laravel-compatible Stage 3 implementation with full payment details
                return (
                  <div className={styles.availableProperties}>
                    {/* Stage 3: Payment Pending (Status 4) - Full Laravel Payment Form */}
                    {isPendingPayment && (
                      <>
                        <h3 className={styles.availablePropertiesTitle}>
                          Property Assignment: {latestRent?.room?.property?.name || 'Property'} ({latestRent?.room?.property?.property_id || 'ID'})
                        </h3>
                        <div className={styles.availablePropertiesText}>
                          Please submit payment in full and monitor our system for updates, as it will automatically reflect when your payment has been processed. Follow the steps to review your licence agreement, note your property address, use your Guardian ID for payments, and confirm your payment by clicking "Payment Submitted". For questions, use the "Have a Query" button at the top left of the page.
                        </div>

                        {/* Laravel Complete Placement Description */}
                        <div className={styles.stageBox}>
                          <p>To complete your guardian placement, please follow these steps</p>
                          <ul>
                            <li><strong>Read Licence Agreement:</strong> Review every page of your licence agreement. If you have any questions, click the "Have a Query" button at the top left of the page to contact us.</li>
                            <li><strong>Property Details:</strong> Note the full address of your new property.</li>
                            <li><strong>Guardian ID:</strong> Keep your unique Guardian ID number for payments.</li>
                            <li><strong>Payment Breakdown:</strong> Review the summary of costs payable. Make payment via bank transfer and click the "Payment Submitted" button to confirm payment.</li>
                          </ul>
                        </div>

                        {/* Payment Submitted Button - Matching Laravel payment_pending.blade.php */}
                        <div className={styles.btnSave} style={{marginTop: '20px'}}>
                          <button
                            type="button"
                            className={styles.btnMyglobeSubmit}
                            onClick={handleStage3PaymentSubmit}
                            disabled={submittingPayment}
                          >
                            {submittingPayment ? 'Submitting…' : 'Payment submitted'} <span style={{marginLeft: '5px'}}>➤</span>
                          </button>
                        </div>

                        {/* License Agreement Section */}
                        <div className={styles.licenseAgreement}>
                          <h4>License Agreement</h4>
                          <div 
                            className={styles.agreementBox}
                            onClick={handleLicenseDownload}
                            style={{cursor: 'pointer'}}
                          >
                            <div className={styles.agreementIcon}>📄</div>
                            <div className={styles.agreementInfo}>
                              <div className={styles.agreementTitle}>Agreement</div>
                              <div className={styles.agreementSubtitle}>Click to open</div>
                            </div>
                          </div>
                          <div className={styles.agreementFrame}>
                            {latestRent?.id ? (
                              <>
                                <iframe
                                  key={`license-iframe-${latestRent.id}`}
                                  src={`${API_BASE_URL}/api/guardian-dashboard/license-agreement/display/${latestRent.id}/license_agreement?token=${localStorage.getItem('token') || ''}`}
                                  title="License Agreement"
                                  style={{
                                    width: '100%',
                                    height: '400px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                  }}
                                  onError={(e) => {
                                    console.error('❌ [IFRAME] Load error:', e);
                                    // Hide iframe on error and show fallback message
                                    const iframe = e.target;
                                    iframe.style.display = 'none';
                                    const existingError = iframe.parentNode.querySelector('.iframe-error');
                                    if (!existingError) {
                                      const errorDiv = document.createElement('div');
                                      errorDiv.className = 'iframe-error';
                                      errorDiv.innerHTML = '<p style="padding: 20px; text-align: center; color: #666;">License agreement preview is not available. Please use the "Agreement" button above to download the PDF document.</p>';
                                      iframe.parentNode.appendChild(errorDiv);
                                    }
                                  }}
                                  onLoad={(e) => {
                                    console.log('✅ [IFRAME] License agreement iframe loaded successfully');
                                    // Remove any existing error messages
                                    const errorDiv = e.target.parentNode.querySelector('.iframe-error');
                                    if (errorDiv) {
                                      errorDiv.remove();
                                    }
                                  }}
                                />
                                {/* Fallback message that will be shown if iframe fails */}
                                <div className="iframe-error" style={{ display: 'none', padding: '20px', textAlign: 'center', color: '#666' }}>
                                  <p>License agreement preview is not available. Please use the "Agreement" button above to download the PDF document.</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <p><strong>License Agreement Preview</strong></p>
                                <p>Your license agreement will be displayed here once rent information is available.</p>
                                <em>Waiting for rent data...</em>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Payment Details Section */}
                        <div className={styles.stageBox}>
                          <p>Below are a summary of costs payable. Please make payment via bank transfer to:</p>
                          
                          {/* Bank Account Details - Laravel-compatible dynamic selection */}
                          <div className={styles.bankDetails}>
                            {dashboardData?.paymentConfig?.bankAccount ? (
                              <>
                                <p><strong>Account Name:</strong> {dashboardData.paymentConfig.bankAccount.name}</p>
                                {dashboardData.paymentConfig.bankAccount.bank && (
                                  <p><strong>Bank:</strong> {dashboardData.paymentConfig.bankAccount.bank}</p>
                                )}
                                <p><strong>Sort Code:</strong> {dashboardData.paymentConfig.bankAccount.sortCode}</p>
                                <p><strong>Account Number:</strong> {dashboardData.paymentConfig.bankAccount.accountNumber}</p>
                                <p><strong>Reference:</strong> {dashboardData?.guardian?.guardian_id || 'Your Guardian ID'}</p>
                              </>
                            ) : (
                              // Fallback to default bank details
                              <>
                                <p><strong>Account Name:</strong> Global Guardians Limited</p>
                                <p><strong>Account Number:</strong> 12345678</p>
                                <p><strong>Sort Code:</strong> 12-34-56</p>
                                <p><strong>Reference:</strong> {dashboardData?.guardian?.guardian_id || 'Your Guardian ID'}</p>
                              </>
                            )}
                          </div>

                          <p>Upon receipt of payment we will contact you.</p>

                          <h4>Next Steps</h4>
                          <ol>
                            <li><strong>Sign Licence Agreement:</strong> Review and sign your licence agreement.</li>
                            <li><strong>Key Collection:</strong> Use the "Have a Query" button at the top left of the page to arrange when and where to collect your keys</li>
                            <li><strong>Move In:</strong> Move into your new property and begin your guardian journey.</li>
                          </ol>
                          <p>If you have any questions, please use the "Have a Query" button at the top left of the page. We look forward to welcoming you to the Global Guardians community.</p>

                          {/* Payment Summary Box - Laravel-compatible conditional components */}
                          <div className={styles.paymentBox}>
                            <div className={styles.paymentHeader}>Payment summary</div>
                            <div className={styles.paymentBody}>
                              <div className={styles.paymentElement}>
                                <div className={styles.paymentDescription}>Monthly License fee:</div>
                                <div className={styles.paymentPrice}>£{latestRent?.license_fee || '500'}</div>
                              </div>
                              <div className={styles.paymentElement}>
                                <div className={styles.paymentDescription}>Key payment:</div>
                                <div className={styles.paymentPrice}>£{latestRent?.damage_security_payment || '250'}</div>
                              </div>
                              
                              {/* Laravel conditional: Only show these components if rent created before newPaymentFlowDate */}
                              {dashboardData?.paymentConfig?.showLegacyPayments && (
                                <>
                                  <div className={styles.paymentElement}>
                                    <div className={styles.paymentDescription}>Council Tax:</div>
                                    <div className={styles.paymentPrice}>£{latestRent?.council_tax || '100'}</div>
                                  </div>
                                  <div className={styles.paymentElement}>
                                    <div className={styles.paymentDescription}>Admin fee:</div>
                                    <div className={styles.paymentPrice}>£{latestRent?.admin_fee || '50'}</div>
                                  </div>
                                  <div className={styles.paymentElement}>
                                    <div className={styles.paymentDescription}>Fire safety pack:</div>
                                    <div className={styles.paymentPrice}>£{latestRent?.fire_safety_pack || '25'}</div>
                                  </div>
                                </>
                              )}
                              
                              <div className={styles.paymentElement}>
                                <div className={styles.paymentDescription}>Your Guardian ID:</div>
                                <div className={styles.paymentPrice}>{dashboardData?.guardian?.guardian_id || 'HSC3U4X89GV6Y'}</div>
                              </div>
                              <div className={styles.paymentElement + ' payment-total'}>
                                <div className={styles.paymentDescription}><strong>TOTAL PAYABLE:</strong></div>
                                <div className={styles.paymentPrice}>£{(Number(latestRent?.damage_security_payment || 0) + Number(latestRent?.license_fee || 0) + Number(latestRent?.admin_fee || 0) + Number(latestRent?.fire_safety_pack || 0) + Number(latestRent?.council_tax || 0))}</div>
                              </div>
                            </div>
                          </div>

                          {/* Second Payment Submit Button (matching Laravel pull-right) */}
                          <div className={styles.paymentSubmission} style={{textAlign: 'right', marginTop: '20px'}}>
                            <button
                              type="button"
                              className={styles.actionButton}
                              onClick={handleStage3PaymentSubmit}
                              disabled={submittingPayment}
                            >
                              {submittingPayment ? 'Submitting…' : 'Payment submitted'}
                              {!submittingPayment && <span> ➤</span>}
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Stage 3: Awaiting Key Collection (Status 5) - License Signature */}
                    {requiresSignature && (
                      <>
                        <h3 className={styles.availablePropertiesTitle}>Final Step to Secure Your New Home!</h3>
                        <div className={styles.availablePropertiesText}>
                          Congratulations on reaching the final step of your journey to securing your chosen property with Global Guardians! To complete your move-in process, please sign your license agreement.
                          <br/><br/>
                          <div className={styles.withCenterButton} style={{margin: '20px 0'}}>
                            <button
                              type="button"
                              className={styles.btnMyglobeSubmit}
                              onClick={() => navigate('/guardians/license-agreement')}
                              style={{fontSize: '18px', padding: '15px 30px'}}
                            >
                              Sign License Agreement ➤
                            </button>
                          </div>
                          <br/>
                          <strong>By signing this agreement, you're one step closer to moving into your new home. Don't wait any longer – complete this final step now!</strong>
                        </div>

                        {/* License Agreement for Signature */}
                        <div className={styles.licenseAgreement}>
                          <h4>Licence Agreement for {user?.first_name || 'Guardian'} {user?.last_name || ''}</h4>
                          
                          {/* Agreement Document */}
                          <div 
                            className={styles.agreementBox}
                            onClick={handleLicenseDownload}
                            style={{cursor: 'pointer'}}
                          >
                            <div className={styles.agreementIcon}>📄</div>
                            <div className={styles.agreementInfo}>
                              <div className={styles.agreementTitle}>Agreement</div>
                              <div className={styles.agreementSubtitle}>Click to open</div>
                            </div>
                          </div>
                          
                          <div className={styles.agreementFrame}>
                            {latestRent?.id ? (
                              <iframe
                                src={`${API_BASE_URL}/api/guardian-dashboard/license-agreement/display/${latestRent.id}/license_agreement?token=${localStorage.getItem('token') || ''}`}
                                title="License Agreement for Signature"
                                style={{
                                  width: '100%',
                                  height: '400px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                            ) : (
                              <>
                                <p><strong>License Agreement Document</strong></p>
                                <p>Your license agreement will be displayed here once rent information is available.</p>
                                <em>Waiting for rent data...</em>
                              </>
                            )}
                          </div>

                          {/* E-Signature Section */}
                          <div className={styles.stageBox}>
                            <h4>Sign the agreement</h4>
                            <div style={{background: '#f5e6a3', padding: '20px', borderRadius: '8px', margin: '20px 0'}}>
                              <p><strong>E-Signature Canvas</strong></p>
                              <canvas
                                ref={canvasRef}
                                width={320}
                                height={200}
                                style={{
                                  background: 'wheat',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  cursor: 'crosshair',
                                  display: 'block',
                                  marginBottom: '15px',
                                  touchAction: 'none',
                                  maxWidth: '100%'
                                }}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                              />
                              <button 
                                type="button" 
                                onClick={clearSignature}
                                style={{background: '#6c757d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', marginBottom: '20px'}}
                              >
                                Clear
                              </button>
                              
                              <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                  Date signed <span style={{color: '#dc3545'}}>*</span>
                                </label>
                                <DateInput
                                  name="signatureDate"
                                  value={signatureDate}
                                  onChange={(e) => setSignatureDate(e.target.value)}
                                  required
                                  className={styles.formInput}
                                />
                              </div>
                            </div>

                            <div className={styles.withCenterButton}>
                              <button
                                type="button"
                                className={styles.btnMyglobeSubmit}
                                onClick={handleLicenseSignatureSubmit}
                                disabled={submittingPayment}
                              >
                                {submittingPayment ? 'Submitting...' : 'Submit documentation'} ➤
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Stage 3: Final Completion Message - Laravel: final-message.blade.php "You're Almost Home!" */}
                    {/* Note: Header content is now in the green header section above via messageDescription */}
                    {isFinalMessage && (
                      <>
                        {/* Property Overview and Key Collection Form - Laravel: final-message.blade.php */}
                        <div className="row" style={{marginTop: '20px'}}>
                          {/* Property Overview Section */}
                          <div className="col-md-5">
                            <div className={styles.panelTitle}>
                              <h4>Your property overview</h4>
                            </div>
                            <div className={styles.panelGroup} style={{border: '1px solid #ddd', borderRadius: '4px', padding: '15px', textAlign: 'center'}}>
                              <dl style={{margin: 0, textAlign: 'left'}}>
                                <dt style={{fontWeight: 'bold', display: 'inline'}}>Property ID:</dt>
                                <dd style={{display: 'inline', marginLeft: '10px'}}>{latestRent?.room?.property?.property_id || '-'}</dd>
                                <br/>
                                <dt style={{fontWeight: 'bold', display: 'inline'}}>Property name:</dt>
                                <dd style={{display: 'inline', marginLeft: '10px'}}>{latestRent?.room?.property?.name || '-'}</dd>
                                <br/>
                                <dt style={{fontWeight: 'bold', display: 'inline'}}>Property address:</dt>
                                <dd style={{display: 'inline', marginLeft: '10px'}}>
                                  {latestRent?.room?.property?.address_1 || '-'}
                                  {latestRent?.room?.property?.address_2 && <><br/>{latestRent.room.property.address_2}</>}
                                  {latestRent?.room?.property?.address_3 && <><br/>{latestRent.room.property.address_3}</>}
                                </dd>
                                <br/>
                                <dt style={{fontWeight: 'bold', display: 'inline'}}>Postcode:</dt>
                                <dd style={{display: 'inline', marginLeft: '10px'}}>{latestRent?.room?.property?.postcode || '-'}</dd>
                              </dl>
                            </div>
                          </div>

                          {/* Arrange Key Collection Form */}
                          <div className="col-md-7">
                            <div className={styles.panelTitle}>
                              <h4>Arrange Key Collection</h4>
                            </div>
                            <div className={styles.panelGroup} style={{border: '1px solid #ddd', borderRadius: '4px', padding: '15px'}}>
                              <form onSubmit={handleKeyCollectionSubmit}>
                                <div className="form-group">
                                  <p style={{marginBottom: '10px'}}>Please write date and time you would like to collect the keys at:</p>
                                  <label htmlFor="keyCollectionDescription" style={{fontWeight: 'bold'}}>
                                    Description <span style={{color: '#dc3545'}}>*</span>
                                  </label>
                                  <textarea
                                    id="keyCollectionDescription"
                                    className="form-control"
                                    rows={5}
                                    value={keyCollectionDescription}
                                    onChange={(e) => setKeyCollectionDescription(e.target.value)}
                                    placeholder="Enter preferred date and time for key collection..."
                                    required
                                  />
                                </div>
                                <div className="text-center" style={{marginTop: '20px'}}>
                                  <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={submittingKeyCollection}
                                  >
                                    {submittingKeyCollection ? (
                                      <>Submitting...</>
                                    ) : (
                                      <>
                                        <i className="glyphicon glyphicon-plus" style={{marginRight: '5px'}}></i>
                                        Submit new ticket
                                      </>
                                    )}
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Stage 3: Awaiting Key Collection (After Payment - Status 4 with payment_confirmed=1) */}
                    {/* Laravel: payment_pending.blade.php shows all content + "Thank you" message but NO payment button */}
                    {isAwaitingKeyCollection && !requiresSignature && !isFinalMessage && (
                      <>
                        {/* Laravel: Thank you message (only shown when paymentConfirmed) */}
                        <div className={styles.stageBox}>
                          <p>Thank you for submitting payment. Our Guardian Management Team is currently working on your application. You will be contacted shortly for the next stage in the vetting process.</p>
                        </div>

                        {/* License Agreement Section - Laravel: always shown on payment_pending.blade.php */}
                        <div className={styles.licenseAgreement}>
                          <div
                            className={styles.agreementBox}
                            onClick={handleLicenseDownload}
                            style={{cursor: 'pointer'}}
                          >
                            <div className={styles.agreementIcon}>📄</div>
                            <div className={styles.agreementInfo}>
                              <div className={styles.agreementTitle}>Agreement</div>
                              <div className={styles.agreementSubtitle}>Click to open</div>
                            </div>
                          </div>
                          <div className={styles.agreementFrame}>
                            {latestRent?.id ? (
                              <iframe
                                key={`license-iframe-confirmed-${latestRent.id}`}
                                src={`${API_BASE_URL}/api/guardian-dashboard/license-agreement/display/${latestRent.id}/license_agreement?token=${localStorage.getItem('token') || ''}`}
                                title="License Agreement"
                                style={{
                                  width: '100%',
                                  height: '400px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                            ) : (
                              <p>License agreement will be displayed here once rent information is available.</p>
                            )}
                          </div>
                        </div>

                        {/* Payment Details Section - Laravel: always shown on payment_pending.blade.php */}
                        <div className={styles.stageBox}>
                          <p>Below are a summary of costs payable. Please make payment via bank transfer to:</p>

                          {/* Bank Account Details */}
                          <div className={styles.bankDetails}>
                            {dashboardData?.paymentConfig?.bankAccount ? (
                              <>
                                <p><strong>Account Name:</strong> {dashboardData.paymentConfig.bankAccount.name}</p>
                                {dashboardData.paymentConfig.bankAccount.bank && (
                                  <p><strong>Bank:</strong> {dashboardData.paymentConfig.bankAccount.bank}</p>
                                )}
                                <p><strong>Sort Code:</strong> {dashboardData.paymentConfig.bankAccount.sortCode}</p>
                                <p><strong>Account Number:</strong> {dashboardData.paymentConfig.bankAccount.accountNumber}</p>
                                <p><strong>Reference:</strong> {dashboardData?.guardian?.guardian_id || 'Your Guardian ID'}</p>
                              </>
                            ) : (
                              <>
                                <p><strong>Account Name:</strong> Global 100 Ltd</p>
                                <p><strong>Account Number:</strong> 20-06-21</p>
                                <p><strong>Sort Code:</strong> 52025503</p>
                              </>
                            )}
                          </div>

                          <p>Upon receipt of payment we will contact you.</p>

                          <h4>Next Steps</h4>
                          <ol>
                            <li>Sign Licence Agreement: Review and sign your licence agreement.</li>
                            <li>Key Collection: Use the "Have a Query" button at the top left of the page to arrange when and where to collect your keys</li>
                            <li>Move In: Move into your new property and begin your guardian journey.</li>
                          </ol>
                          <p>If you have any questions, please use the "Have a Query" button at the top left of the page. We look forward to welcoming you to the Global Guardians community.</p>

                          {/* Payment Summary Box */}
                          <div className={styles.paymentBox}>
                            <div className={styles.paymentHeader}>Payment summary</div>
                            <div className={styles.paymentBody}>
                              <div className={styles.paymentElement}>
                                <div className={styles.paymentDescription}>Monthly License fee:</div>
                                <div className={styles.paymentPrice}>£{latestRent?.license_fee || '0'}</div>
                              </div>
                              <div className={styles.paymentElement}>
                                <div className={styles.paymentDescription}>Key payment:</div>
                                <div className={styles.paymentPrice}>£{latestRent?.damage_security_payment || '0'}</div>
                              </div>

                              {/* Laravel: legacy payments only shown if rent created before newPaymentFlowDate */}
                              {dashboardData?.paymentConfig?.showLegacyPayments && (
                                <>
                                  <div className={styles.paymentElement}>
                                    <div className={styles.paymentDescription}>Council Tax:</div>
                                    <div className={styles.paymentPrice}>£{latestRent?.council_tax || '0'}</div>
                                  </div>
                                  <div className={styles.paymentElement}>
                                    <div className={styles.paymentDescription}>Admin fee:</div>
                                    <div className={styles.paymentPrice}>£{latestRent?.admin_fee || '0'}</div>
                                  </div>
                                  <div className={styles.paymentElement}>
                                    <div className={styles.paymentDescription}>Fire safety pack:</div>
                                    <div className={styles.paymentPrice}>£{latestRent?.fire_safety_pack || '0'}</div>
                                  </div>
                                </>
                              )}

                              <div className={styles.paymentElement}>
                                <div className={styles.paymentDescription}>Your Guardian ID:</div>
                                <div className={styles.paymentPrice}>{dashboardData?.guardian?.guardian_id || '-'}</div>
                              </div>
                              <div className={`${styles.paymentElement} payment-total`}>
                                <div className={styles.paymentDescription}><strong>TOTAL PAYABLE:</strong></div>
                                <div className={styles.paymentPrice}>£{(Number(latestRent?.damage_security_payment || 0) + Number(latestRent?.license_fee || 0) + Number(latestRent?.admin_fee || 0) + Number(latestRent?.fire_safety_pack || 0) + Number(latestRent?.council_tax || 0))}</div>
                              </div>
                            </div>
                          </div>

                          {/* No payment button here - payment already confirmed */}
                        </div>
                      </>
                    )}
                  </div>
                );
              }

              // For declined/blacklisted statuses, don't show available spaces
              if (applicationStage?.current === 'declined') {
                return (
                  <div className={styles.availableProperties}>
                    <h3 className={styles.availablePropertiesTitle}>
                      Sorry {user?.first_name || 'Guardian'}!
                    </h3>
                    <div className={styles.availablePropertiesText}>
                      Your request has been declined.
                    </div>
                  </div>
                );
              }

              if (applicationStage?.current === 'blacklisted') {
                return (
                  <div className={styles.availableProperties}>
                    <h3 className={styles.availablePropertiesTitle}>
                      Your account is blacklisted
                    </h3>
                    <div className={styles.availablePropertiesText}>
                      You are no longer able to apply to become a property guardian with us. If you do have any queries, please email:{' '}
                      <a href="mailto:guardiancare@global-guardians.co.uk">
                        guardiancare@global-guardians.co.uk
                      </a>
                    </div>
                  </div>
                );
              }

              // Property assigned specific content  
              if (statusContent.showPropertyAssignedMessage) {
                return (
                  <div className={styles.availableProperties}>
                    <h3 className={styles.availablePropertiesTitle}>
                      Property Assigned
                    </h3>
                    <div className={styles.availablePropertiesText}>
                      Thank you for choosing the property and attending a viewing. We will contact you shortly to advise about the next steps.
                    </div>
                    <div className={styles.stageBox}>
                      You have selected this property. Please wait until we send you an email.
                    </div>
                  </div>
                );
              }

              // Default - no available spaces section
              console.log('ℹ️ [DASHBOARD] No available spaces section for status:', dashboardData?.guardian?.status_id);
              return null;
            })()}
          </div>
          )}

          {/* License Signature Section for Status 5 (awaiting_key_collection) - rendered separately since applicationProcess is hidden */}
          {isAwaitingKeyCollection && !isAwaitingKeyCollectionSigned && (
              <div className={styles.licenseAgreement}>
                <h4>Licence Agreement for {user?.first_name || 'Guardian'} {user?.last_name || ''}</h4>

                {/* Agreement Document */}
                <div
                  className={styles.agreementBox}
                  onClick={handleLicenseDownload}
                  style={{cursor: 'pointer'}}
                >
                  <div className={styles.agreementIcon}>📄</div>
                  <div className={styles.agreementInfo}>
                    <div className={styles.agreementTitle}>Agreement</div>
                    <div className={styles.agreementSubtitle}>Click to open</div>
                  </div>
                </div>

                <div className={styles.agreementFrame}>
                  {latestRent?.id ? (
                    <iframe
                      src={`${API_BASE_URL}/api/guardian-dashboard/license-agreement/display/${latestRent.id}/license_agreement?token=${localStorage.getItem('token') || ''}`}
                      title="License Agreement for Signature"
                      style={{
                        width: '100%',
                        height: '400px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  ) : (
                    <>
                      <p><strong>License Agreement Document</strong></p>
                      <p>Your license agreement will be displayed here once rent information is available.</p>
                      <em>Waiting for rent data...</em>
                    </>
                  )}
                </div>

                {/* E-Signature Section */}
                <div className={styles.stageBox}>
                  <h4>Sign the agreement</h4>
                  <div style={{background: '#f5e6a3', padding: '20px', borderRadius: '8px', margin: '20px 0'}}>
                    <p><strong>E-Signature Canvas</strong></p>
                    <canvas
                      ref={canvasRef}
                      width={320}
                      height={200}
                      style={{
                        background: 'wheat',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'crosshair',
                        display: 'block',
                        marginBottom: '15px',
                        touchAction: 'none',
                        maxWidth: '100%'
                      }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    />
                    <button
                      type="button"
                      onClick={clearSignature}
                      style={{background: '#6c757d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', marginBottom: '20px'}}
                    >
                      Clear
                    </button>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        Date signed <span style={{color: '#dc3545'}}>*</span>
                      </label>
                      <DateInput
                        name="signatureDate"
                        value={signatureDate}
                        onChange={(e) => setSignatureDate(e.target.value)}
                        required
                        className={styles.formInput}
                      />
                    </div>
                  </div>

                  <div className={styles.withCenterButton}>
                    <button
                      type="button"
                      className={styles.btnMyglobeSubmit}
                      onClick={handleLicenseSignatureSubmit}
                      disabled={submittingPayment}
                    >
                      {submittingPayment ? 'Submitting...' : 'Submit documentation'} ➤
                    </button>
                  </div>
                </div>
              </div>
          )}

          {/* Key Collection Form for Status 5 with signed license - Laravel: final-message.blade.php */}
          {isAwaitingKeyCollectionSigned && (
            <div className="row" style={{marginTop: '20px'}}>
              {/* Property Overview Section */}
              <div className="col-md-5">
                <div className={styles.panelTitle}>
                  <h4>Your property overview</h4>
                </div>
                <div className={styles.panelGroup} style={{border: '1px solid #ddd', borderRadius: '4px', padding: '15px', textAlign: 'center'}}>
                  <dl style={{margin: 0, textAlign: 'left'}}>
                    <dt style={{fontWeight: 'bold', display: 'inline'}}>Property ID:</dt>
                    <dd style={{display: 'inline', marginLeft: '10px'}}>{latestRent?.room?.property?.property_id || '-'}</dd>
                    <br/>
                    <dt style={{fontWeight: 'bold', display: 'inline'}}>Property name:</dt>
                    <dd style={{display: 'inline', marginLeft: '10px'}}>{latestRent?.room?.property?.name || '-'}</dd>
                    <br/>
                    <dt style={{fontWeight: 'bold', display: 'inline'}}>Property address:</dt>
                    <dd style={{display: 'inline', marginLeft: '10px'}}>
                      {latestRent?.room?.property?.address_1 || '-'}
                      {latestRent?.room?.property?.address_2 && <><br/>{latestRent.room.property.address_2}</>}
                      {latestRent?.room?.property?.address_3 && <><br/>{latestRent.room.property.address_3}</>}
                    </dd>
                    <br/>
                    <dt style={{fontWeight: 'bold', display: 'inline'}}>Postcode:</dt>
                    <dd style={{display: 'inline', marginLeft: '10px'}}>{latestRent?.room?.property?.postcode || '-'}</dd>
                  </dl>
                </div>
              </div>

              {/* Arrange Key Collection Form */}
              <div className="col-md-7">
                <div className={styles.panelTitle}>
                  <h4>Arrange Key Collection</h4>
                </div>
                <div className={styles.panelGroup} style={{border: '1px solid #ddd', borderRadius: '4px', padding: '15px'}}>
                  <form onSubmit={handleKeyCollectionSubmit}>
                    <div className="form-group">
                      <p style={{marginBottom: '10px'}}>Please write date and time you would like to collect the keys at:</p>
                      <label htmlFor="keyCollectionDescription" style={{fontWeight: 'bold'}}>
                        Description <span style={{color: '#dc3545'}}>*</span>
                      </label>
                      <textarea
                        id="keyCollectionDescription"
                        className="form-control"
                        rows={5}
                        value={keyCollectionDescription}
                        onChange={(e) => setKeyCollectionDescription(e.target.value)}
                        placeholder="Enter preferred date and time for key collection..."
                        required
                      />
                    </div>
                    <div className="text-center" style={{marginTop: '20px'}}>
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={submittingKeyCollection}
                      >
                        {submittingKeyCollection ? (
                          <>Submitting...</>
                        ) : (
                          <>
                            <i className="glyphicon glyphicon-plus" style={{marginRight: '5px'}}></i>
                            Submit new ticket
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
        );
      })()}

      {/* Guardian History Table - Only for Previous Guardian (status 7) - Matching Laravel */}
      {(() => {
        const guardian = dashboardData?.guardian;
        const statusId = guardian?.status_id;
        const statusContent = getStatusSpecificContent(guardian, applicationStage, dashboardData?.latestRent);
        
        // Only show guardian history table for previous guardian (status 7) when showResources is true
        if (statusId === 7 && statusContent.showResources) {
          const formatDate = (dateString) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toLocaleDateString('en-GB');
            } catch {
              return dateString;
            }
          };

          const formatStatus = (status) => {
            if (status === null || status === undefined) return '-';
            
            // Handle numeric status values (Laravel rent status: 0=pending, 1=active, 2=failed, 3=ended)
            if (typeof status === 'number') {
              const statusMap = {
                0: 'Pending',
                1: 'Active',
                2: 'Failed',
                3: 'Ended'
              };
              return statusMap[status] || `Status ${status}`;
            }
            
            // Handle string status values
            if (typeof status === 'string') {
              return status.charAt(0).toUpperCase() + status.slice(1);
            }
            
            // Fallback for other types
            return String(status);
          };

          // Filter and paginate guardian rents
          const filteredRents = guardianRents.filter(rent => {
            if (!guardianHistorySearch) return true;
            const searchLower = guardianHistorySearch.toLowerCase();
            return (
              (rent.room?.property?.name || '').toLowerCase().includes(searchLower) ||
              (rent.room?.property?.property_id || '').toLowerCase().includes(searchLower) ||
              (rent.room?.property?.postcode || '').toLowerCase().includes(searchLower) ||
              (rent.room?.name || '').toLowerCase().includes(searchLower) ||
              formatDate(rent.date_start).toLowerCase().includes(searchLower) ||
              formatDate(rent.date_end).toLowerCase().includes(searchLower) ||
              formatStatus(rent.status).toLowerCase().includes(searchLower)
            );
          });

          const startIndex = (guardianHistoryPage - 1) * guardianHistoryEntriesPerPage;
          const endIndex = startIndex + guardianHistoryEntriesPerPage;
          const paginatedRents = filteredRents.slice(startIndex, endIndex);
          const totalPages = Math.ceil(filteredRents.length / guardianHistoryEntriesPerPage);

          const handleViewLicence = async (rentId, documentType = 'license_agreement') => {
            try {
              const presignedUrl = await rentsService.getLicenceDocument(rentId, documentType);
              if (!presignedUrl) {
                throw new Error('No presigned URL returned from server');
              }
              
              // Method 1: Try direct download using blob (preferred method)
              try {
                const response = await fetch(presignedUrl);
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const blob = await response.blob();
                
                // Create blob URL and download link
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${documentType}_${rentId}.pdf`;
                link.style.display = 'none'; // Hide the link
                
                // Append to body
                document.body.appendChild(link);
                
                // Trigger download
                link.click();
                
                // Clean up after a delay
                setTimeout(() => {
                  if (document.body.contains(link)) {
                    document.body.removeChild(link);
                  }
                  window.URL.revokeObjectURL(blobUrl);
                }, 200);
                
                toast.success(`${documentType === 'license_agreement' ? 'License' : 'Signed license'} agreement downloaded`);
                return;
              } catch (blobError) {
                console.warn('Blob download failed, trying direct URL:', blobError);
                // Fallback to Method 2: Direct URL open
              }
              
              // Method 2: Fallback - Open presigned URL directly in new tab
              const link = document.createElement('a');
              link.href = presignedUrl;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              link.download = `${documentType}_${rentId}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              toast.success(`${documentType === 'license_agreement' ? 'License' : 'Signed license'} agreement opened`);
            } catch (error) {
              console.error(`Error downloading ${documentType}:`, error);
              toast.error(`Failed to download ${documentType === 'license_agreement' ? 'license' : 'signed license'} agreement: ${error.message}`);
            }
          };

          return (
            <section className={styles.mainContent}>
              <div className={styles.container}>
                <div className="row">
                  <div className="col-md-12">
                    <div className="panel-title title-standard" style={{ marginBottom: '20px' }}>
                      <h3>Guardian history</h3>
                    </div>
                    <div className="panel-group border-down margin-down">
                      <div className="dataTable_wrapper">
                        {/* Search and pagination controls */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label>Show:</label>
                            <select
                              value={guardianHistoryEntriesPerPage}
                              onChange={(e) => {
                                setGuardianHistoryEntriesPerPage(Number(e.target.value));
                                setGuardianHistoryPage(1);
                              }}
                              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label>Search:</label>
                            <input
                              type="text"
                              value={guardianHistorySearch}
                              onChange={(e) => {
                                setGuardianHistorySearch(e.target.value);
                                setGuardianHistoryPage(1);
                              }}
                              placeholder="Search guardian history..."
                              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '200px' }}
                            />
                          </div>
                        </div>

                        {/* Guardian History Table */}
                        <table className="table table-hover dt-responsive" style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Property</th>
                              <th>ID</th>
                              <th>Postcode</th>
                              <th>Room</th>
                              <th>From</th>
                              <th>To</th>
                              <th>GO</th>
                              <th>Status</th>
                              <th>Licences</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingGuardianRents ? (
                              <tr>
                                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>Loading guardian history...</td>
                              </tr>
                            ) : paginatedRents.length === 0 ? (
                              <tr>
                                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>No guardian history found</td>
                              </tr>
                            ) : (
                              paginatedRents.map((rent) => (
                                <tr key={rent.id}>
                                  <td>{rent.id}</td>
                                  <td>
                                    {rent.room?.property?.name ? (
                                      <a
                                        href={`/properties/${rent.room.property.id}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          navigate(`/properties/${rent.room.property.id}`);
                                        }}
                                        style={{ color: '#17264b', textDecoration: 'underline' }}
                                      >
                                        {rent.room.property.name}
                                      </a>
                                    ) : '-'}
                                  </td>
                                  <td>{rent.room?.property?.property_id || '-'}</td>
                                  <td>{rent.room?.property?.postcode || '-'}</td>
                                  <td>{rent.room?.name || '-'}</td>
                                  <td>{formatDate(rent.date_start)}</td>
                                  <td>{formatDate(rent.date_end)}</td>
                                  <td>
                                    {rent.guardianOfficer ? `${rent.guardianOfficer.first_name || ''} ${rent.guardianOfficer.last_name || ''}`.trim() : '-'}
                                  </td>
                                  <td>
                                    {(() => {
                                      // Handle both numeric (0,1,2,3) and string status values
                                      const statusValue = rent.status !== undefined ? rent.status : (rent.status_id !== undefined ? rent.status_id : null);
                                      const statusNum = typeof statusValue === 'number' ? statusValue : 
                                        (statusValue === 'active' ? 1 : 
                                         statusValue === 'ended' ? 3 : 
                                         statusValue === 'failed' ? 2 : 
                                         statusValue === 'pending' ? 0 : null);
                                      
                                      // Determine badge class based on status
                                      let badgeClass = 'default';
                                      if (statusNum === 1) badgeClass = 'success'; // active
                                      else if (statusNum === 3) badgeClass = 'default'; // ended
                                      else if (statusNum === 2) badgeClass = 'danger'; // failed
                                      else if (statusNum === 0) badgeClass = 'warning'; // pending
                                      
                                      return (
                                        <span className={`label label-${badgeClass}`}>
                                          {formatStatus(rent.status)}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {/* Laravel Logic: Show "Licence" button if license_agreement exists, Show "Signed" button if signed_license_agreement exists */}
                                    {(() => {
                                      const hasLicenseAgreement = rent.license_agreement || rent.licence_agreement;
                                      const hasSignedLicenseAgreement = rent.signed_license_agreement;
                                      
                                      if (!hasLicenseAgreement && !hasSignedLicenseAgreement) {
                                        return '-';
                                      }
                                      
                                      return (
                                        <>
                                          {/* "Licence" button - shows if license_agreement exists (matching Laravel buttons.blade.php) */}
                                          {hasLicenseAgreement && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-info btn-action"
                                              onClick={() => handleViewLicence(rent.id, 'license_agreement')}
                                              style={{ 
                                                marginRight: '4px',
                                                backgroundColor: '#5bc0de',
                                                borderColor: '#5bc0de',
                                                color: 'white'
                                              }}
                                            >
                                              Licence
                                            </button>
                                          )}
                                          
                                          {/* "Signed" button - shows if signed_license_agreement exists (matching Laravel buttons.blade.php) */}
                                          {hasSignedLicenseAgreement && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-info btn-action"
                                              onClick={() => handleViewLicence(rent.id, 'signed_license_agreement')}
                                              style={{ 
                                                backgroundColor: '#5bc0de',
                                                borderColor: '#5bc0de',
                                                color: 'white'
                                              }}
                                            >
                                              Signed
                                            </button>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {filteredRents.length > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px' }}>
                            <div>
                              Showing {startIndex + 1} to {Math.min(endIndex, filteredRents.length)} of {filteredRents.length} entries
                            </div>
                            <div>
                              <button
                                onClick={() => setGuardianHistoryPage(prev => Math.max(1, prev - 1))}
                                disabled={guardianHistoryPage === 1}
                                className="btn btn-sm btn-default"
                                style={{ marginRight: '5px' }}
                              >
                                Previous
                              </button>
                              <span style={{ margin: '0 10px' }}>
                                Page {guardianHistoryPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setGuardianHistoryPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={guardianHistoryPage === totalPages}
                                className="btn btn-sm btn-default"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        }
        return null;
      })()}

      {/* ✅ IMPLEMENTATION 2: Resources Section - Conditional Display */}
      {(() => {
        const statusContent = getStatusSpecificContent(dashboardData?.guardian, applicationStage, dashboardData?.latestRent);
        
        if (statusContent.showResources) {
          return (
            <section className={styles.resourcesSection}>
        <div className={styles.resourcesContainer}>
          <h3 className={styles.resourcesTitle}>My resources</h3>
          <div className={styles.resourcesGrid}>
            {/* Left column */}
            <div className={styles.resourcesCol}>
              <div className={styles.resourceCard}>
                <div className={styles.resourceCardTitle}>
                  Useful information & documentation
                </div>
                <p
                  onClick={() => navigate('/useful-information')}
                  className={styles.resourceLink}
                >
                  Library of Global Guardian documentation. <i>&gt;</i>
                </p>
              </div>
              <div className={styles.resourceCard}>
                <div className={styles.resourceCardTitle}>
                  Recommend a friend
                </div>
                <div className={styles.resourceText}>
                  Know someone who might be interested in becoming a guardian?
                </div>
                <form onSubmit={handleRecommendSubmit}>
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder="Friend's name"
                      value={recommendForm.name}
                      onChange={(e) =>
                        setRecommendForm({
                          ...recommendForm,
                          name: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <input
                      type="tel"
                      className={styles.formInput}
                      placeholder="Friend's phone number (10-15 digits)"
                      value={recommendForm.phone_number}
                      onChange={handlePhoneNumberChange}
                      pattern="[0-9+\-\s()]*"
                      maxLength="20"
                      title="Please enter a valid phone number (10-15 digits, numbers, spaces, hyphens, plus, parentheses only)"
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <input
                      type="email"
                      className={styles.formInput}
                      placeholder="Friend's email"
                      value={recommendForm.email}
                      onChange={(e) =>
                        setRecommendForm({
                          ...recommendForm,
                          email: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className={styles.withCenterButton}>
                    <button
                      type="submit"
                      className={styles.btnMyglobeSubmit}
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting...' : 'Submit details'}{' '}
                      <i>&gt;</i>
                    </button>
                  </div>
                </form>
              </div>
            </div>
            {/* Right column */}
            <div className={styles.resourcesCol}>
              <div className={styles.resourceCard}>
                <div className={styles.resourceCardTitle}>
                  Global Guardian newsletter
                </div>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.newsletterCheckbox}
                    checked={newsletterChecked}
                    onChange={(e) => handleNewsletterChange(e.target.checked)}
                  />
                  Receive our newsletter
                </label>
              </div>
              <div className={styles.resourceCard}>
                <div className={styles.resourceCardTitle}>Voluntary work</div>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.newsletterCheckbox}
                    checked={voluntaryWorkChecked}
                    onChange={(e) =>
                      handleVoluntaryWorkChange(e.target.checked)
                    }
                  />
                  Receive information on any voluntary work.
                </label>
              </div>
              <div className={styles.resourceCard}>
                <div className={styles.resourceCardTitle}>
                  Empty property tip
                </div>
                <div className={styles.resourceText}>
                  Have you seen an empty property that you feel can be occupied
                  by Property Guardians? Let us know the details and we will get
                  in contact with the owners. If we take on the building, you
                  may be in for a monetary reward!
                </div>
                <div className={styles.withCenterButton}>
                  <button
                    onClick={handleEmptyPropertyTip}
                    className={styles.btnMyglobeSubmit}
                  >
                    Submit details <i>&gt;</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
          );
        } else {
          // Don't render resources section for blacklisted/declined guardians
          console.log('🚫 [DASHBOARD] Resources section hidden for status:', dashboardData?.guardian?.status_id);
          return null;
        }
      })()}

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.container}>
          Copyright 2025 Global Guardians. All rights reserved
        </div>
      </footer>
    </div>
  );
};

export default GuardianDashboard;
